// 台詞IDはメモの参照先。改稿時も振り直さず、削除したIDを再利用しない。
// 全面改稿した話の旧IDは再利用しない。局所改稿はIDを維持し、追加行に枝番、章の再構成にr501以降を使用。
export const STORY_REVISION = 'story-editor-2026-11-04.1';
export const STORY_EDITION = 'story-polished-v4';
export type StoryTrack = 'main' | 'akari' | 'kazamaru' | 'suzune' | 'yuzuha';
export interface StoryScene { background: string; location: string; cast: string[] }
export type StoryMoment = 'intro' | 'aftermath' | 'enter' | 'fort' | 'castle' | 'clear' | 'ending';
export const STORY_MOMENT_LABELS: Record<StoryMoment, string> = { intro: '里の初戦前', aftermath: '里の初戦後', enter: '入国', fort: '前線の砦攻略後', castle: '本城戦前', clear: '国制圧後', ending: '十国統一後' };
/** ストーリーの曲の種類（台本の `@bgm|種類` でその行から切り替える。指定が無い所は storyBgmOf の決まりで選ぶ） */
// boss＝合戦のボス戦の曲（物語の中の戦いの場面）
export type StoryBgm = 'daily' | 'warm' | 'journey' | 'tense' | 'mystery' | 'sad' | 'resolve' | 'triumph' | 'boss';
export const STORY_BGMS: StoryBgm[] = ['daily', 'warm', 'journey', 'tense', 'mystery', 'sad', 'resolve', 'triumph', 'boss'];
export interface StoryLine { id: string; speaker: string; text: string; scene: StoryScene; moment?: StoryMoment; bgm?: StoryBgm }
export interface StoryEpisode extends StoryScene {
  id: string; track: StoryTrack; title: string; lines: StoryLine[];
}
export const STORY_TRACKS: { id: StoryTrack; label: string; title: string; description: string; char?: string }[] = [
  { id: 'main', label: '本編', title: '魔王と四人の幼なじみ', description: '里の襲撃で蘇ったのは、かつて百鬼の王だった記憶。四人の幼なじみと、ばらばらになった国を取り戻す。' },
  { id: 'akari', label: 'アカリ外伝', title: '王様を叱る灯', description: '雪国での再会のあと。旧臣の前で、いつもの呼び方ができなくなった。', char: 'akari' },
  { id: 'kazamaru', label: 'カザマル外伝', title: '落ちこぼれの帰り道', description: '天狗の山へ出発する前。故郷から来た手紙を、三日間開けられなかった。', char: 'kazamaru' },
  { id: 'suzune', label: 'スズネ外伝', title: 'この槌で、隣に立つ', description: '焔谷への遠征前。皆の道具を直すうち、自分の槌を後回しにしていた。', char: 'suzune' },
  { id: 'yuzuha', label: 'ユズハ外伝', title: '狐が選んだ主', description: '竹林の戦のあと。里へ来た日の嘘を、まだ一つだけ話せずにいる。', char: 'yuzuha' },
];
function ep(id: string, track: StoryTrack, title: string, location: string, background: string, cast: string[], script: string): StoryEpisode {
  let scene: StoryScene = { location, background, cast };
  const lines: StoryLine[] = [];
  let moment: StoryMoment | undefined;
  let bgm: StoryBgm | undefined;
  for (const row of script.trim().split('\n')) {
    const [key, speaker, ...parts] = row.split('|');
    if (key === '@moment') { moment = speaker as StoryMoment; continue; }
    if (key === '@bgm') {
      if (!STORY_BGMS.includes(speaker as StoryBgm)) throw new Error(`${id}: 不明な曲 @bgm|${speaker}`);
      bgm = speaker as StoryBgm;
      continue;
    }
    if (key === '@scene') {
      scene = { background: speaker, location: parts[0], cast: (parts[1] ?? '').split(',').map(id => id.trim()).filter(Boolean) };
    } else {
      lines.push({ id: `${id}.${key}`, speaker, text: parts.join('|'), scene, moment, ...(bgm ? { bgm } : {}) });
    }
  }
  return { id, track, title, location, background, cast, lines };
}
/** その行で流す曲：台本の @bgm、無ければ場面から（外伝＝ぬくもり、入国＝新天地、砦・本城の前＝緊張、制圧後＝ぬくもり、統一後＝凱歌、序章の朝＝日常） */
export function storyBgmOf(ep: Pick<StoryEpisode, 'track'>, line: Pick<StoryLine, 'bgm' | 'moment'>): StoryBgm {
  if (line.bgm) return line.bgm;
  if (ep.track !== 'main') return 'warm';
  switch (line.moment) {
    case 'intro': return 'daily';
    case 'enter': return 'journey';
    case 'fort':
    case 'castle': return 'tense';
    case 'ending': return 'triumph';
    default: return 'warm';
  }
}
export const STORY_EPISODES: StoryEpisode[] = [
  ep('main-00', 'main', '序章　百鬼の王、目を覚ます', '青葉国・潮見地方・紅葉の里', 'village', ['akari', 'suzune'], `
@moment|intro
@bgm|daily
r001|narrator|紅葉の里の朝。あなたは社の屋根に上り、スズネに頼まれた瓦を運んでいた。下ではアカリが朝飯の支度をしている。
r002|akari|冷めるよー。最後の一枚にして、降りておいで。
r002n1|player|あと二枚。昨日、雨が落ちたところだけ終わらせる。
r002n2|akari|二枚って、さっきも言ってなかった？　下で数えてるからね。
r002n3|narrator|庭には、雨漏りを受ける桶が三つ干してあった。一つはまだ底から水が漏れている。直す物は、屋根の上にも下にもあった。
r003|suzune|待て待て。それは置いとけ。昨日も同じところ踏み抜いただろ。
r004|player|今日は落ちない。足の置き場は覚えた。
r005|narrator|瓦が滑った。襟首をつかまれ、あなたの足が宙に浮く。
r006|suzune|明日も覚えててくれよ。
r006n1|player|わかってる。……今度は、本当に。
r006n2|suzune|瓦は持ってきてくれればいい。上で踏ん張るのは、あたしの役だ。
r006n3|narrator|スズネはあなたを梯子へ戻し、片手で瓦を受け取った。あなたは下から屋根を押さえる板を運んだ。
r007|narrator|里で育った。力仕事では、幼なじみの四人にいつも助けられていた。
r007n1|narrator|まだ赤子のころ、社の石段で泣いているところを里の者に拾われたという。拾われた日のことは知らない。雨漏りの位置なら、目を閉じていても指させた。
r007n2|player|ユズハ、頼まれた帳面、社の中に置いてある。帰ったら見てくれ。
r007n3|yuzuha|先に見ました。一つだけ計算が違いましたよ。今夜、一緒に直しましょう。
r007n4|akari|だったら、今日は早く帰ってきてね。五人揃わないと、スズネが先に食べちゃう。
@bgm|tense
r008|kazamaru|飯はあとだ！　西から兵が来る。狸の旗、五十はいるぞ！
r009|yuzuha|子供たちは社の裏へ。アカリ、道を照らしてあげて。
r009n1|narrator|鍋の蓋が落ちた。拾う暇もなくアカリが走り、スズネは屋根から飛び降りた。ユズハが開けた社の戸へ、裸足の子供たちが駆け込んでくる。
r009n2|player|俺は鐘を鳴らす。西の畑にいる人を呼び戻してくれ！
r009n3|kazamaru|わかった。門には近づくなよ。今のあいつら、止まりそうにねえ！
@scene|village|紅葉の里・西の門|akari,r01
r010|narrator|門前に狸の軍勢が並んだ。青葉を治める化け狸の頭領は、兵に井戸を囲ませ、里の奥にある社へ鉄扇を向けた。
r011|r01|この里は今日からわしのものだ。社の下を掘る。邪魔な家は壊せ。
r012|player|社に何がある。
r013|r01|お前に説明する義理はない。物の値打ちも知らぬ里には、過ぎた宝よ。
r013n1|player|掘るなら、せめて里の者が出るまで待て。家を壊す理由にはならないだろ。
r013n2|r01|待てば、誰かが先に持ち出す。里ごと押さえれば、宝は逃げぬ。
r013n3|akari|ここに住んでる人がいるんだよ。あんたの荷物置き場じゃない！
r014|suzune|なら、持って帰れるか試してみろ！
r015|narrator|大槌を頭領が鉄扇で受けた。跳ね返った柄がスズネの肩を打つ。カザマルの風も、敵の鎧をかすめるだけだった。
r016|kazamaru|くそっ、硬え！　ユズハ、後ろ！
r016n1|suzune|あたしは動ける。肩を見てる暇があったら、門の横を頼む！
r016n2|narrator|あなたは倒れた柵を引き寄せた。兵の足が一瞬止まる。その間に、畑から戻った二人が社の裏へ走り込んだ。
r016n3|yuzuha|あと一人、井戸の横にいます。カザマル、上から見つけて！
r017|narrator|ユズハの刀が弾かれる。アカリが張った火の壁を、頭領は一歩で踏み越えた。
r018|akari|こっちに来ちゃだめ！　あんたまで捕まったら……！
r018n1|player|お前だけここへ残して、隠れていられるか。
r018n2|narrator|アカリの提灯が揺れた。火の薄くなった場所へ鉄扇が迫る。あなたは彼女の腕をつかみ、柵の陰へ引いた。
r019|narrator|アカリを庇った肩を、振り下ろされた鉄扇が裂いた。血が、社から続く古い石畳へ落ちた。
r019v1|narrator|石畳の溝を血が伝い、赤い光が灯った。肩の痛みが遠のく。アカリの呼ぶ声へ返事をしようとしたのに、口からは、自分でも知らないはずの名がこぼれた。
@scene|capital|三百年前・百鬼の都|
@bgm|mystery
r020|narrator|その名を呼んだ記憶が、目の前に重なった。鬼も天狗も狐も、玉座の前に集まっている。あなたはその席から皆を見下ろしていた。一人残らず、名前を知っていた。
r021|player|……俺の、城。
r022|narrator|配下の名を呼ぶ。差し出された手に触れる。その妖力が膨れ上がる感触を、身体が覚えていた。かつて百鬼を束ねた王の手だった。
r022n1|narrator|名を呼ばれた者が、次々に立ち上がった。嬉しそうな顔も、怖がる顔もあった。手を取る前に、必ず相手の返事を待っていた。
r022n2|player|力を押し込むんじゃない。……開くんだ。あいつらが、自分で使えるように。
r022n3|narrator|広間が赤く染まる。ひび割れた床。扉の向こうで、誰かがあなたを呼んでいた。振り返ろうとした途端、記憶は焼き切れた。
@scene|village|紅葉の里・西の門|akari,r01
@bgm|boss
r023|r01|まだ立つか、小僧が。
r024|player|その足をどけろ。
r025|narrator|石畳に赤い紋が走った。頭領が後ずさる。あなたには見える。四人の胸の奥に、まだ使われていない力が眠っている。
r025n1|r01|その紋は……。いや、社の石が動いただけだ。お前が動かしたはずはない！
r025n2|narrator|肩の痛みは消えていなかった。足にも力は入らない。それでも、四人へ何を渡せるのかだけは、昔の指先が覚えていた。
r026|player|アカリ。俺の声を聞け。
r027|akari|聞こえてる！　だから、もう無茶しないで！
r028|player|眠っている力を開く。お前の火で、みんなを守れる。俺を信じてくれるか。
r029|akari|……今さら、それ聞く？
r029n1|akari|やって。みんなを社まで通せるなら、私が受け止める。
r029n2|player|無理なら言え。俺の声が聞こえている間は、止められる。
r030|narrator|提灯が白く燃えた。アカリの足元から広がった炎が、里の者を避け、敵の槍だけを溶かしていく。
r030n1|akari|熱くない……。どこへ火を置けばいいか、こんなにはっきり見える！
r030n2|narrator|アカリは子供の前へ灯りを一つ置いた。炎は道の両側に分かれ、走る者の袖には触れなかった。
r031|player|カザマル、スズネ、ユズハ！　お前たちもだ。受け取れ！
r031n1|suzune|どんとこい！　その代わり、あとで仕組みを説明しろよ。槌まで壊れそうだ！
r031n2|yuzuha|私も。今は、逃げる人を通すほうを優先します。
r032|kazamaru|うおっ……翼が、軽い！
r033|suzune|槌が勝手に浮くぞ。これ、どこまで振っていいんだ！？
r034|yuzuha|門の外へ向けて。味方まで飛ばさないでね。
r034v1|narrator|あなたが声を張るたび、四人の印が脈打った。まだ誰にも気づかれないように、震える左手を握り込んだ。
r034n1|narrator|四人があなたを中心に立ち直った。頭領は鉄扇を持ち直したが、もう社へまっすぐ進める道はなかった。
r034n2|player|門の外へ押し返す。里の中で追い回すな。みんなの帰る家を、残すぞ！
r034n3|player|アカリ、正面を頼む。俺が道を示す。三人は、里に残った兵を追い出してくれ。
@moment|aftermath
@bgm|resolve
r035|narrator|カザマルの突風が兵の列を割り、ユズハの狐火が退路を塞ぐ。スズネが一振りすると、頭領の鉄扇は真ん中から折れた。
r036n1|kazamaru|まだやるか？　こっちは、まだ飛び足りねえぞ。
r036n2|yuzuha|カザマル、屋根の上の兵を先に。降りる場所を空けてあげて。
r036|r01|眷属を……一度に四人も。そんな術、昔話にしか……。お前、何者だ。
r037|player|……思い出した。俺は、百鬼の王だ。
r037n1|narrator|頭領は、あなたの足元に走る紋を見下ろした。折れた鉄扇が、震える手から落ちた。
r038|narrator|頭領は兜を捨てて逃げた。兵たちもあとを追う。追撃を命じようとしたところで、あなたの膝が折れた。
r038n1|narrator|倒れる寸前、スズネの腕が脇へ入った。四つの印はまだ灯っていたが、あなたから流れていた力は、細く弱くなっていた。
r038n2|akari|追わなくていい！　こっちへ戻って！　今は、この人を運ぶのが先！
@scene|village|紅葉の里・社の縁側／夕方|akari,suzune
@bgm|warm
r039|narrator|目が覚めると、肩には包帯が巻かれていた。アカリが冷めた粥を膝に置く。
r039n1|player|里は。あいつら、戻ってこなかったか。
r039n2|akari|見張りを置いた。捕まった人も戻ってる。家は壊れたけど、今ここで直しに行こうとしないでね。
r039n3|suzune|王様の力、しばらく残ってた。門を動かすのには助かったけど、加減を覚えないと危ねえな。
r039n4|player|加減を覚える。お前たちを強くした途端、俺が寝ていたんじゃ、格好がつかん。
r040|player|……よくやった。我が配下にふさわしい働きだ。
r041|akari|それ言う前に、口開けて。
r042|suzune|王様でも、飯は食うんだな。
r043|player|お前たち、少しは驚かないのか。
r044|yuzuha|驚いていますよ。朝は屋根から落ちかけていた人が、あの百鬼の王だなんて。
r045|kazamaru|あれは落ちた。スズネが拾った。
r045n1|player|昔は……いや、今朝のことまで変わるわけじゃないな。
r045n2|yuzuha|王様だった証拠なら、もっと欲しいですね。今朝の帳面、一桁ずれていましたから。
r045n3|akari|それは明日。今日は寝るの。昔の話も、聞きたいけど明日でいい。
r046|narrator|笑うと肩が痛んだ。三百年前の玉座より、この狭い縁側のほうが、今はずっと手放したくなかった。
r046n1|narrator|あなたは粥を食べ終え、空の椀を返した。門の修理をする音が遠くに聞こえた。今夜だけは、その仕事を誰かに任せて目を閉じた。
`),
  ep('main-01', 'main', '第一話　最初の城は、裏口から', '青葉国', 'meadow', ['akari', 'r01'], `
@moment|enter
@scene|village|青葉国・潮見地方・紅葉の里|akari,yuzuha
@bgm|journey
r201|narrator|里を襲った化け狸の頭領は、青葉城へ逃げた。あなたたちは壊れた門を立て直し、見張りを決めてから社へ集まった。
r201a|suzune|門は直った。でも次もあいつが来るなら、板を厚くしたって無駄だぞ。
r202|yuzuha|潮見の砦で、また兵を集めています。隣の村は米俵を三つ取られました。その前の村では、若い人が二人連れていかれています。
r202a|akari|ここを守るだけじゃ、別の村が同じ目に遭うんだ。
r203|akari|出かけている間、里は大丈夫？　四人ともいなくなるんだよ。
r203a|player|攻めるのは、カザマルが見てきた街道からだ。里へ続く道は塞がせない。何かあっても、留守の者が逃げられる。
r204|player|潮見の砦を押さえて、青葉城へ行く。兵も食料も、もう集めさせない。
r204b|player|……これより、百鬼の王の国盗りを始める。皆の者、ついてまいれ。
r204c|akari|はいはい。お弁当、いくつ作ればいい？
r204d|yuzuha|五つです。王様の分は、少なめで。
r204a|narrator|ユズハが地図へ小石を二つ置いた。里に近い潮見の砦と、山一つ向こうの青葉城だった。
r205|suzune|城まで取るのか。五人で？　壁を壊すなら、かなりかかるぞ。
r205a|kazamaru|上から見てくる。屋根に穴があったら、そこから入ろうぜ。
r206|player|その前に、古い井戸を探してくれ。俺が掘らせた裏口がある。まだ塞がれていなければな。
r206a|narrator|あなたは記憶を頼りに井戸の場所を書き込んだ。四人は地図を覗き込み、誰からともなく荷物をまとめ始めた。
@moment|fort
@scene|meadow|青葉国・潮見地方・潮見砦|suzune,r01
@bgm|tense
r207|narrator|砦の門が落ちた。頭領は倉へ逃げ込もうとしたが、先に回ったカザマルに行く手を塞がれた。
r207a|kazamaru|おい、そっちは食料だろ。火をつけて逃げる気か！
r208|r01|わしの倉だ！　何をしようと勝手だろうが！
r208a|suzune|袋に里の焼き印があるぞ。いつからお前のものになった。
r209|narrator|スズネが倉の閂を引き抜いた。頭領は彼女の脇をすり抜け、今度は坂道へ逃げる。残された兵たちは、槍を地面へ置いた。
r209a|akari|追う前に、倉を開けよう。中から声がする。
r210|yuzuha|荷運びに捕らえた人たちですね。帰る道を確保します。本城へ進むのは、それからです。
r210a|player|武器を置いた兵も通せ。捕らえられた者を運ぶ手が要る。
r211|suzune|ところで、城の裏口って何のために掘ったんだ？　兵を逃がすためか？
r211a|narrator|あなたが黙ると、四人の顔が一斉にこちらを向いた。井戸の場所だけは、やけにはっきり思い出せていた。
r212|player|城の外の酒蔵へ通うためだ。……今、役に立つ。
r212a|yuzuha|では、酒蔵側から探しましょう。立派な理由を考えている間に、塞がれてしまいます。
@moment|castle
@scene|meadow|青葉国・青葉地方・青葉城の井戸|suzune,r01
r213|narrator|井戸の底からスズネが顔を出した。抜け道は残っていた。ただし、三百年分の泥も残っていた。
r213a|narrator|隣の小屋から囚人がこちらを見ていた。あなたが口へ指を当てると、その人は頷き、寝ている仲間を静かに起こした。
r214|suzune|王様。帰ったら、この服も洗ってくれよ。井戸の石まで真っ黒だ。
r214a|akari|先に手を貸して。降りる人が滑らないように、ここへ灯りを置くね。
r215|r01|なぜ内側にいる！　鍵はわしが持っているのに！
r215a|narrator|頭領が本丸から駆け出してきた。新しい鉄扇を振り開くと、井戸へ向かう人々の足が止まった。
r216|yuzuha|鍵が一つなら、井戸までは思いつきませんね。皆さん、こちらへ。下に縄があります。
r216a|r01|待て！　そいつらには、社を掘らせる仕事が残っておる！
r217|player|もうお前の命令は聞かせない。スズネ、井戸の前へ。俺たちが道を空ける。
r217a|r01|地下の王印を手に入れれば、他の城も従う。小さな里一つのために、邪魔をするな！
r218|player|その里に、俺たちが住んでいる。お前が踏んだ家のことを、少しは覚えて帰れ。
r218a|narrator|最後の囚人が縄へ手をかけた。アカリが井戸の縁から離れ、あなたの隣で提灯を掲げた。
@moment|clear
@scene|meadow|青葉国・青葉地方・青葉城の本丸|akari,r01
@bgm|warm
r219|narrator|鉄扇が床を滑った。頭領は追おうとして、扇より近くにアカリの火があることに気づき、両手を上げた。
r219a|akari|まだ動かないで。……里の家を壊したときも、そうやって火を怖がってくれればよかったのに。
r220|r01|城も蔵も渡す！　兵も引かせる！　だから、それを消せ！
r220a|yuzuha|蔵は村ごとに返す物が違います。帳面と、保管場所を全部出してください。
r221|player|潮見と青葉の兵を退け。奪った物を返したら、壊した家の修理だ。お前にも働いてもらう。
r221a|r01|王印より先に、家の話か。……印は玉座の下だ。里の社にも、対になる石がある。
r222|narrator|玉座の下の石箱に、社の石畳と同じ紋があった。触れると、あなたの掌に十の灯が浮かぶ。九つはすぐに消え、一つだけが暖かく残った。
r222a|player|国々へ声を届ける印だ。昔、何度も使った。……最後に何を伝えたかだけ、思い出せん。
r222av1|narrator|印の縁に耳を寄せると、途切れた声がした。『すぐ戻る』。あなたの声だった。聞き直そうとすると、十の灯が、もう一度だけ明滅した。
r223|yuzuha|光を見て、向こうから使いが来るでしょう。よその国では、昔の百鬼の王を『魔王』と呼ぶそうですから。お祝いばかりとは思えません。
r223b|player|魔王か。……悪くない響きだ。
r223c|akari|喜ばないの。それ、悪口だからね。
r223a|narrator|その日のうちに、蔵の奥から別の国の荷札が山ほど出てきた。一つの米俵に、三つも四つも通行の札が貼り重ねてある。
r223av1|suzune|一つの俵が、何回買われてるんだ。村へ届く頃には、倍の値だぞ。
r223av2|r01|札を払わねば、隣の領主が舟を止める。わし一人が兵を退いても、次の国までは約束できぬ。
r223av3|akari|なら、またあの門で待つの？　今度は誰の旗が来るか、って。
r223av4|narrator|あなたは地図を広げた。昔は一つだった国が、川と峠で細切れになっている。里の名は、指の先で隠れるほど小さかった。
@bgm|resolve
r223av5|player|次の兵が来る前に、俺が行く。
r223av6|yuzuha|城を取れば、その先にも境がありますよ。どこでやめるおつもりですか。
r224|player|青葉だけでは終われない。十の城を取る。俺たちの旗が見えたら、荷車が止まらずに済む国にする。
r224v1|kazamaru|……全部かよ。王様って、本当に欲張りなんだな。
r224v2|player|ああ。昔の城も見たい。お前たちを連れて、堂々と表から入ってみたい。裏口だけ覚えているのは、格好が悪い。
r224v3|suzune|なら、今度は五人が並べるくらい広いやつにしろ。あの井戸、狭かったぞ。
r224a|narrator|地図の端を押さえるあなたの手に、まだ血の染みがあった。アカリはその手へ包帯を巻き直した。反対はしなかった。きつく結んだので、少し痛かった。
`),
  ep('main-02', 'main', '第二話　魔王は川を渡れない', '川霧国', 'river', ['kazamaru', 'e020'], `
@moment|enter
@scene|river|川霧国・水穂地方・渡し場|kazamaru,e020
@bgm|tense
r201|narrator|里の薬箱が底をついた。買い付けの荷が止められていると知り、あなたたちは川霧の渡し場へ向かった。米俵まで岸に積まれ、青い匂いを放っていた。
r201a|akari|これ、まだ届いてなかったんだ。帰りを待ってる人がいるのに。
r202|e020|魔王の旗がついた荷は三倍だ。よその王が通るたびに、川を好きに使われてたまるか。
r202a|yuzuha|昨日出した舟まで止めたそうですね。先払いした渡し賃も返していない。
r203|player|昨日までの値に戻せ。船頭たちも返してもらう。流れを知る者を閉じ込めたまま、誰に舟を出させる気だ。
r203a|e020|あいつらは、俺に黙って荷を通そうとした。勝手を許せば、ここは他国の裏口になる。
r204|kazamaru|舟を使わなけりゃ文句ねえだろ。おれが運べば――うわっ！
r204a|narrator|岸から飛び立った翼の下で、水面が盛り上がった。牛鬼が鎖を跳ね上げ、カザマルの足首へ巻きつける。
r205|narrator|スズネが差し出した槌に、カザマルがしがみついた。岸の石が二つ抜けるほど引き合って、ようやく鎖が外れた。
r205a|suzune|先に言え！　あたしまで泳ぐところだったぞ！
r206|e020|空も川の上なら俺の目が届く。文句があるなら、水穂の水門を越えてこい。
r206a|player|岸から離れろ。足を引かれたら、助けに入った者も沈む。あの鎖を切る場所から探すぞ。
@moment|fort
@scene|river|川霧国・水穂地方・水穂砦の水門|kazamaru,suzune
@bgm|warm
r207|narrator|水門の巻き上げ機を押さえると、砦を囲んでいた水が引いた。退く牛鬼の足音に紛れて、操作小屋から戸を叩く音がした。
r207a|narrator|カザマルが閂を外すと、痩せた船頭が転がり出てきた。仲間は城の牢へ移されたが、水門を扱える自分だけが残されたという。
r208|kazamaru|悪い。力が増えたから、何でも運べる気になってた。
r208a|suzune|運べたって、行き先で引っ張られたら終わりだ。次は縄を一本つけとくか？
r209|player|俺も同じだ。昔の力があれば、川ごと押し返せると考えていた。だが、下流には村がある。
r209a|yuzuha|船頭さん、水を一度に逃がすと下流が危ないそうです。……この杭ですね。ここまで下がれば、城前を歩けると。
r210|akari|仲間はまだ城にいるんだね。待ってて。薬の荷も、みんなも、一緒に連れて帰る。
r210a|player|だから、舟を沈めるような攻め方はできない。水が落ち着くまで待とう。
r211|suzune|この印まで下がれば、歩ける。船頭が毎年、杭へ刻んでたんだな。
r211a|narrator|やがて泥の中から杭の先が現れた。カザマルは地図にそれを足し、濡れた指で牢の場所を示した。
r212|kazamaru|浅瀬のすぐ上を飛ぶ。鎖を投げる腕も、今度はこっちから見える。
r212a|akari|私もそこを照らす。速く行くより、ちゃんと帰ってきてね。
@moment|castle
@scene|river|川霧国・川霧地方・川霧城の浅瀬|kazamaru,e020
@bgm|tense
r213|narrator|浅瀬の先に牛鬼が立った。低くなった水を踏み、鎖の端を両岸の杭へ渡している。城へ進む道は、その鎖の間にしかない。
r213a|e020|操作小屋を開けたな。あいつめ、牢を出るなり、余計なことを喋ったか。
r214|e020|だが、川底を知ったくらいで俺を倒せると思うな。ここでは、足を滑らせたほうが終わりだ。
r214a|kazamaru|さっき引っ張られた分、よく見たよ。その鎖、巻き戻すときは片手が塞がる。
r215|yuzuha|牢は対岸の小屋です。鍵を取ってきますから、あの人の目をこちらへ向けておいて。
r215a|suzune|扉が開いたら、あたしが岸まで運ぶ。濡れてる板には乗るなって、先に言っとけよ。
@bgm|resolve
r216|player|アカリ、岸まで灯りを繋げ。カザマルは合図を待て。あの腕が上がったら、水を吹き上げろ。
r216a|akari|濡れても消えないように、小さくするね。船頭さんたち、これを目印に帰れる。
r217|kazamaru|目くらましなら、川一本分あるな。牛鬼、さっきのお返しだ！
r217a|narrator|牛鬼が鎖を持ち上げた。カザマルは高く逃げず、流れに沿って翼を開く。低い風が水面に白い線を引いた。
r218|e020|おい、そっちへ水を飛ばすな！　目に――！
r218a|player|ユズハ、今だ。俺たちは浅瀬を押さえる。帰る道を一本、ここへ残すぞ。
@moment|clear
@scene|river|川霧国・川霧地方・川霧城の船着き場|kazamaru,e020
@bgm|warm
r219|narrator|勝負が決まったあと、一人の船頭が濡れた板を踏み外した。牛鬼は鎖を捨て、誰より先に川へ飛び込んだ。
r219a|narrator|引き上げられた船頭は咳き込みながら、牛鬼の胸を一度だけ叩いた。牛鬼は黙ってその背を支え、岸へ上げた。
r220|player|その腕を借りたい。今度は、舟を通すために。鎖で止めるより、お前にしかできない仕事だろう。
r220a|e020|俺が降りれば、ほかの国の兵が好きに渡る。渡した先で何をするかまで、見ていられんぞ。
r221|player|兵を通すときは行き先を知らせる。知らせのない軍なら止めろ。俺の旗でも同じだ。
r221a|yuzuha|三倍払った商人が、こちらを見ていますよ。約束の話だけで帰ったら、私たちまで恨まれそうです。
r222|e020|……返す。返せばいいんだろう。いちいち、そんな顔で見るな。
r222a|narrator|牛鬼が銭袋を岸へ投げた。船頭が拾い、足りないと指を二本立てる。牛鬼は腰のもう一袋を、今度は叩きつけた。
r223|narrator|牛鬼は岸の杭から鎖を外した。水穂から川霧へ、薬を積んだ最初の舟が出る。カザマルは、真ん中の席を選んだ。
r223b|narrator|あなたは舟の縁を両手でつかんでいた。昔から、流れる水だけはどうにも苦手だった。
r223c|kazamaru|百鬼の王が、渡し舟で青い顔してんのかよ。
r223d|player|……昔は、橋を架けさせてから渡っていた。
r223a|akari|端じゃなくていいの？　上から見えなかった景色、よく見えるよ。
r224|kazamaru|今日は足を乾かす日なの。おれが飛ばなくても運んでくれるんだし、任せとく。
r224a|narrator|渡しの中ほどで、カザマルが船頭へ浅瀬の名前を尋ねた。船頭は竿で下流を指し、牛鬼に落とされたことのある荷の話まで教えてくれた。
`),
  ep('main-03', 'main', '第三話　狐の嘘と、帰る場所', '風越国', 'bamboo', ['yuzuha', 'r03'], `
@moment|enter
@scene|bamboo|風越国・若竹地方・竹の小径|yuzuha,r03
@bgm|mystery
r201|narrator|風越の竹を売りに来ていた荷車が、一台も里へ来なくなった。閉ざされた街道を開くため竹林に入ると、帰り道が消えた。道標の向こうで、竹の葉が刃のように舞った。
r201a|kazamaru|さっきから同じ石を三回見てる。あれ、飛び越えても戻されるぞ。
r202|r03|迷わせたいわけじゃない。九尾様の使いだ。ユズハ、迎えに来た。
r202a|narrator|ユズハは返事をせず、刀の鯉口に親指をかけた。普段なら真っ先に道を調べる彼女が、一歩も動かなかった。
r203|yuzuha|迎えが要ると、お伝えした覚えはありません。
r203a|r03|報告が途絶えたからな。あの里での役目はもう終わりだと、九尾様は仰せだ。
r204|akari|役目って、何。ユズハ、この人を知ってるの？
r204a|yuzuha|……私を里へ行かせた方の、家臣です。話します。ただ、ここでは待ってください。
r205|player|ユズハは、今は俺たちと来ている。連れて帰りたいなら、本人の話を聞け。
r205a|r03|その子の話を聞くのは、あんたが先じゃないのか。知らないことが、まだ多そうだ。
r206|narrator|鎌鼬が鎌を払うと、風が竹を割り、その隙間に砦が見えた。ユズハは石へ刀で印を刻み、引き返す道だけは失わないようにした。
r206a|yuzuha|若竹の砦に術の札があります。そこを止めれば、道が戻ります。……先頭は、私に任せてください。
@moment|fort
@scene|bamboo|風越国・若竹地方・若竹砦|yuzuha,akari
@bgm|sad
r207|narrator|砦の札が燃え、同じ場所を巡っていた道が繋がった。鎌鼬の気配は風越城へ遠ざかる。ユズハは追わず、刀を膝へ置いた。
r207a|narrator|アカリが水筒を差し出した。ユズハは両手で受け取り、栓の紐を何度も巻き直した。栓は開かない。アカリは水筒の底を支えたまま、隣に座っていた。
r208|yuzuha|九尾様は、私を里へ置いた人です。社の地下にある、昔の王の遺物を見張れ、と。
r208a|player|社で俺が暮らしていることも、知らせていたのか。
r209|yuzuha|はい。出入りする者を調べました。皆さんと親しくなれば、社へも入れると思って。
r209a|akari|……あの日、雨宿りしてたのも？　偶然、あそこにいたんじゃなかったんだ。
r210|yuzuha|待っていました。でも、王印が目覚めた報告は出していません。今も、出すつもりはありません。
r210a|narrator|アカリは水筒から手を離した。少し離れて座り直したが、ユズハの隣には荷物一つ分の場所を空けた。
r211|player|……俺が寝ている間も、見ていたのか。
r211a|yuzuha|はい。裏道も、見張りの人数も、知らせました。古い報告が城に残っています。取り戻させてください。
r212|player|俺も行く。お前一人で行かせたら、帰ってこない気がする。
r212a|narrator|ユズハがようやく栓を開けた。飲んだのは一口だけだった。残りを膝へ置き、地図の裏へ知っていることを書き始めた。
@moment|castle
@scene|bamboo|風越国・風越地方・風越城の竹門|yuzuha,r03
@bgm|tense
r213|narrator|城門には、里の社と同じ戸が現れていた。鎌鼬はその縁側に座り、ユズハが来ると、隣の空いた場所を鎌の柄で叩いた。
r213a|r03|そんな怖い顔をするな。ここへ戻れば、一族の者があんたを責めることはない。九尾様もそう仰せだ。
r214|r03|王も友達も連れてくればいい。九尾様の城で、今までどおり暮らせばいいだろう。
r214a|yuzuha|今までどおりなら、どうして道を閉じたのです。帰る先を、私が決めてはいけないからでしょう。
r215|r03|外へ出した子を、そのまま敵に取られるほど、お人好しじゃないんでな。
r215a|player|俺の隣にいる者を、お前の持ち物として数えるな。ユズハが戻るなら止めない。だが、帰らせるための戦には負けられん。
r216|r03|それで、その子の嘘も全部引き受けるのか。
r216a|player|まだ話の途中だ。お前に連れていかれたら、続きを聞けなくなる。
@bgm|resolve
r217|yuzuha|里に残れと言われたからではありません。残りたかったんです。九尾様へ出す手紙を、何度も書き直すくらいには。
r217a|narrator|ユズハは偽物の戸へ刀を当てた。見慣れた社の景色が裂け、その奥から城門の鉄の金具が現れる。
r218|r03|九尾様の術のほどき方まで、そっちへ持っていくか。なら遠慮はいらねえ。知っているだけで破れるか、試してみろ。
r218a|yuzuha|アカリ、灯りを低く。影が二つある所を教えて。そこから開きます。
@moment|clear
@scene|bamboo|風越国・風越地方・風越城の門外|yuzuha,r03
@bgm|warm
r219|narrator|門を覆っていた幻が消えた。鎌鼬はユズハの刀を見つめ、構えかけていた鎌を下ろした。
r219a|r03|国の印は渡す。だが、ここの狐も鼬も、九尾様を頼って暮らしている。全員を里へ追い立てるつもりか。
r220|player|街道の術を解け。荷車を通して、ここへ来た者を勝手に帰れなくするな。それと、ユズハに出していた命令は全部取り消すよう、九尾に伝えろ。
r220a|r03|その子の報告書を読んだのか。……いや、まだだな。本人が話すまで待つつもりか。
r221|yuzuha|私の報告書も返してください。何を知らせたかは、私から皆さんに話します。
r221a|narrator|鎌鼬は奥の廊下を示した。ユズハが一人で行こうとすると、アカリが包みを手に隣へ並んだ。
@scene|bamboo|風越国・風越地方・風越城の廊下|yuzuha,akari
r222|akari|ユズハ。里へ来た日にくれた団子も、仕事だった？
r222a|yuzuha|……はい。皆さんが好きだと、調べていました。
r223|akari|二回目の、形が崩れてたやつは。あれも調べて買ってきたの？
r223a|yuzuha|あれは、私が作ったんです。お金が足りなくて。でも、また持っていきたくて。
r224|narrator|アカリは包みを開き、団子を一本渡した。笑って済ませるには、まだ聞きたいことが多かった。二人は食べ終えてから、書庫へ入った。
r224a|player|帰り道は、急がなくていい。荷物も話も、置いていくなよ。
`),
  ep('main-04', 'main', '第四話　玉座のない王', '白鷺国', 'temple', ['akari', 'r04'], `
@moment|enter
@scene|temple|白鷺国・古鐘地方・霊廟の参道|akari,r04
@bgm|mystery
r201|narrator|白鷺には、王が消えた日の記録が残るという。あなたは古鐘の霊廟へ向かった。門に並ぶ配下の名をなぞると、自分の筆の癖が指先へ戻ってきた。
r201a|akari|知ってる人の名前？　ずっとここを掃除してたみたい。苔がついてない。
r202|r04|止まれ。百鬼の王の御領である。名を持たぬ者を、この奥へは通さぬ。
r202a|narrator|門の影で骨が鳴った。骸骨武者は、古い鎧を身につけ、参道の両側を見渡す位置に立っていた。
r203|player|俺だ。戻った。この字も、この門を建てた日のことも、覚えている。
r203a|r04|あの日、王は最後に何と申された。
r204|player|俺の名を一番大きく彫るな、と。……なのに、まだ真ん中にあるな。
r204a|narrator|骸骨武者の指が止まった。あなたの顔から、四人の手に灯る印へと視線が移る。戸口に立つその背が、わずかに強張った。
r205|r04|王印は本物。記憶もある。だが、そのお身体では、門の内へ逃げ込む者さえ守れますまい。
r205a|kazamaru|見た目で決めんなよ。こっちには、おれたちもいる。
r206|r04|ならば古鐘の守りを越えられよ。ここを任せる相手を、言葉だけで決めるわけにはいかぬ。
r206a|player|わかった。門の名は削るな。俺たちが入るまで、そいつらの場所を守っていろ。
@moment|fort
@scene|temple|白鷺国・古鐘地方・古鐘砦の奥|akari,r04
r207|narrator|砦の守りが崩れると、骸骨武者は石室の扉を開けた。奥には玉座が一つ。座面から伸びた紋が、床一面へ刻まれていた。
r207a|player|この座は……知っている。ここで配下へ力を与え、ここへ戻した。誰より強くなるために。
r208|r04|配下へ分けた妖力を戻せば、かつてのように戦えましょう。王ご自身が前へ立てる。
r208a|suzune|返したら、あたしらは元に戻るんだな。倒れたりはしないのか。
r209|akari|近づいただけで、火が小さくなった。……大丈夫だけど、勝手に引っ張られる感じがする。
r209a|narrator|あなたが一歩下がると、火は元の大きさへ戻った。石室の紋には、一人分の足跡しか残っていなかった。
r210|player|俺が座れば、力は戻る。だが、前に出るのも俺一人になる。
r210a|r04|最後の災いの夜、この座も光りました。配下の力が、一度に都へ引かれていったのです。……その先のことは、我らも知らされておりません。
r211|suzune|それで王様が帰ってこなくなったら、待つ側は何もできないだろ。
r211a|narrator|石の肘掛けには、深い指の跡が残っていた。あなたはその窪みへ手を合わせた。今の指では、端まで届かなかった。
r212|player|……今は座らん。こいつらの力は、こいつらに持たせておく。
r212a|r04|今は、と申されましたな。では、白鷺城でお待ちします。その答えが変わらぬかどうか、見せていただく。
@moment|castle
@scene|temple|白鷺国・白鷺地方・白鷺城の奥の院|suzune,r04
@bgm|tense
r213|narrator|奥の院の前で、骸骨武者が刀を抜いた。背後には古い位牌が並ぶ。あなたは、刀が振り上がるときの肩の動きを覚えていた。
r213a|player|右へ誘って、左から払う。昔、稽古で何度も食らった。
r214|r04|よく覚えておいでだ。ですが、その足では避けきれますまい。四人を含めて、今の王の力と見なします。
r214a|suzune|そのほうがいい。こっちだって、黙って後ろで見てるつもりはないからな。
r215|akari|私が止められるのは、一撃ぶん。続けて受けるなら、間をあけて。
r215a|yuzuha|横へ回る場所はあります。カザマル、私が抜けるまで風を抑えてください。
@bgm|resolve
r216|player|俺が刀を読む。カザマルは横へ逸らせ。スズネ、受けるのは一度だけでいい。次は踏み込め。
r216a|kazamaru|聞いたな。二回目まで頑張ったら、逆に邪魔だからな！
r217|r04|昔は、ご自身が最初に踏み込まれた。今も、その足は前へ出かかっていますぞ。
r217a|narrator|あなたは片足を戻した。アカリが横目でそれを確かめ、提灯を少し高く持ち直す。
r218|player|癖だ。……来い。今日は、後ろから見たお前の隙も覚えて帰る。
r218a|narrator|刀が上がった。あなたが声を出すより半拍早く、四人が動き始めた。稽古の記憶にない風が、広間を横切った。
@moment|clear
@scene|temple|白鷺国・白鷺地方・白鷺城の山門|akari,r04
@bgm|warm
r219|narrator|骸骨武者が刀を下ろした。折れた鎧の紐を結び直す手を止め、あなたではなく、四人へ深く頭を垂れた。
r219a|suzune|ちょっと待て。その結び方だと、腕を上げたら抜ける。こっち、貸してみろ。
r220|r04|王を、お頼み申す。ご自分の傷に気づくのが、どうにも遅いお方ゆえ。
r220a|akari|知ってます。包帯の場所を増やすたび、四人で叱ってるところです。
r221|player|俺への挨拶は、それだけか。もう少し、戻ってきたことを喜んでもいいだろう。
r221a|r04|喜んでおります。だからこそ、次にお送りするときは、戻る手段まで確かめておきたい。
r222|player|門は開けておけ。俺を待つために、ほかの者まで締め出すな。ここを頼る者は、今もいるんだろう。
r222a|narrator|門の外には、参拝の荷を抱えた人が待っていた。骸骨武者が脇へ退くと、何年ぶりかと柱に触れてから入っていった。
r222av1|narrator|帰り際、あなたは記録の最後を確かめた。『都の地下で炎が止む。王の消息、知れず』。骸骨武者も、それ以上のことは知らなかった。
@bgm|daily
r223|narrator|奥から運ばれてきた昔の外套は、今のあなたには長すぎた。着せたがる骸骨武者と、裾をつまむアカリの間で、あなたは動けなくなった。
r223a|r04|裾をお引きずりになれば……いえ、もう少しお育ちになれば、あるいは。
r224|akari|今日は肩に掛けるだけにしよう。帰ったら裏から留める。ちゃんと歩けるほうが大事でしょ。
r224a|narrator|あなたが外套を掛けて門をくぐると、骸骨武者は追ってこなかった。参道を行く人たちへ向き直り、今度は門を開けるために手を伸ばした。
`),
  ep('main-05', 'main', '第五話　待っていた人、待てなかった人', '白嶺国', 'snow', ['akari', 'e050'], `
@moment|enter
@scene|snow|白嶺国・霜月地方・雪の峠道|akari,e050
@bgm|journey
r501|narrator|白嶺から届いた招待状には、米の買い付けと南への道を開く条件が記されていた。末尾の『お待ちしております』だけが、墨を重ねて書かれていた。
r502|narrator|峠の迎えは雪女一人だった。あなたの顔を見た途端、雪の上へ膝をつく。呼ばれた昔の名に、胸の奥がわずかに痛んだ。
r503|e050|お戻りになったのですね。声だけでも、もう一度聞きたかった。
r504|player|……戻った。だが、まだ思い出せないことが多い。お前を待たせた理由も。
r505|e050|お話は城で。お部屋も、昔お好きだった菓子も、用意させてあります。
r506|akari|よかったね。手紙を読んでから、ずっと――。
@bgm|tense
r507|narrator|雪女の袖が動いた。アカリの足元へ、細い氷の壁が走る。雪女はあなたにだけ手を差し出し、四人のほうへは目も向けない。
r508|e050|お招きしたのは、王お一人です。眷属を連れて他国を攻める旅は、ここでおやめください。
r509|player|そういう条件は、手紙になかった。
r510|e050|書けば、おいでにならなかったでしょう。ここに留まってくだされば、国の印も蔵も、お望みの物をすべてお渡しします。
r511|narrator|橇を引く者が、峠の下で足を止めた。荷台には眠った子供が二人。薪の束は、子供の下に敷けば終わる量だった。
r512|player|俺を閉じ込めて、冬を越す薪が増えるか。南へ買いに行く道を開けろ。そのために来たんだ。
r513|e050|王が都へ行かれたあの冬も、印からお声が届きました。すぐ戻る、と。……私はもう、待てません。
r514|akari|帰るかどうかは、この人が決めることだよ。私たちが邪魔なら、私を見て言って。
r515|narrator|雪女はようやくアカリを見た。提灯の火を、まるで知らない傷でも見るように。氷がもう一段高くなり、橇の子供が泣き出した。
r516|player|アカリ、火を下げろ。子供に近い。スズネ、氷を割る。話の続きは、道が開いてからだ。
@moment|fort
@scene|snow|白嶺国・霜月地方・霜月砦|akari,e050
r517|narrator|砦の柵が崩れ、閉じられていた橇道が見えた。アカリは退く兵を追わず、雪に埋まった山小屋へ走った。中から戸を叩く音がしていた。
r518|suzune|柱には火を当てるな！　屋根ごと落ちる！　横を掘るから、そこだけ照らせ！
r519|narrator|小さな隙間から子供の手が出た。あなたが引き、スズネが梁を支える。アカリは自分の火が雪へ触れないよう、肘を胸へ押しつけていた。
r520|e050|その火なら、戸ごと焼けたでしょう。なぜ、そんな遠回りを。
r521|akari|中が見えなかったから！　……見えないところまで、大丈夫って決められないもの。
r522|narrator|最後に出てきた子が、アカリの袖をつかんだ。寒さで固まった指を一本ずつ開かせる間、雪女は戸口に立ったまま見ていた。
r523|player|薪の蔵はどこだ。鍵をよこせ。
r524|e050|あれは城の冬越しの分です。ここで使えば、奥の者の火が。
r525|player|目の前で凍えている。今、これを黙って見ていたら、俺はお前の城にも入れない。
@bgm|warm
r526|narrator|雪女は首に下げた鍵を外した。差し出す手から、なかなか指が離れなかった。あなたは引かずに待った。
r527|e050|城にも、あなたのための火を残してあります。三百年、一度も消していません。
r528|akari|……それ、この子たちにも使ってあげて。お城で待ってる人が、こんなに寒そうなの、嫌だと思う。
r529|narrator|雪女が鍵を放した。子供の肩へ外套を掛け、こちらを見ずに坂を登っていく。アカリは、その背中が吹雪へ消えるまで見送った。
r530|player|ありがとう。だが、城へ行くぞ。俺の帰りを待つために、ほかの者をここへ閉じ込めさせはしない。
@moment|castle
@scene|snow|白嶺国・白嶺地方・白嶺城|akari,e050
@bgm|tense
r531|narrator|白嶺城の門は、内側から凍りついていた。城下へ出ようとした橇が、何台も止まっている。雪女は門の上に立ち、降りてこようとはしない。
r532|e050|その四人と離れられぬなら、皆さんもお住まいになればよい。争いに出なければ、誰も失わずに済むでしょう。
r533|player|城の中の者は、出たがっている。俺も同じだ。お前の心配を鎮めるためだけに、ここへ残ることはできない。
r534|e050|今のあなたは、ご自分の弱さをご存じない。傷を負い、倒れてからでは、戻る約束もできないのです。
r535|narrator|雪女が扇を握り締めた。あなたの肩の包帯を見ている。アカリがその視線に気づき、自分で結んだ結び目へ手をやった。
r536|akari|私が、間に合わなかったから？
r537|e050|一度で済むと思わないことです。守れなかった日に、その火で何を温めるのです。
r538|narrator|アカリは口を開き、何も言えずに閉じた。あなたが前へ出ると、彼女の提灯が袖へ当たった。まだ離すつもりはないらしかった。
r539|player|アカリがいたから、俺は今ここにいる。最初の傷も知らずに、こいつを責めるな。
r540|e050|……私も、昔はそう言っていただいた。
r541|narrator|風が止んだ。雪女の目の縁で、小さな氷が光った。城の奥から、子供が門を叩く音が聞こえる。
@bgm|resolve
r542|player|道を開けろ。そうすれば、お前の所へ帰ってくる道も残る。
r543|e050|また帰ってくる。その言葉で、何度眠れぬ夜を越したと……！
r544|narrator|吹雪が門を覆った。アカリは一度目を閉じ、それから提灯を低く持った。炎を大きくすれば、城の中まで焼いてしまう。
r545|akari|私、あの人と同じになるの、怖い。でも、ここであんたを閉じ込めるのは、もっと嫌だ。
r546|player|行こう。門を割る。城の中から出てくる者へ、火を向けるな。
@moment|clear
@scene|snow|白嶺国・白嶺地方・白嶺城の御殿|akari,e050
@bgm|warm
r547|narrator|門の氷が割れ、最初の橇が南へ走った。雪女は追わなかった。手に残った国の印を見つめ、やがてあなたの前へ置いた。
r548|e050|……止められませんでしたね。昔も、今も。
r549|player|俺はまだ、ここにいる。勝ったからと、明日すぐ発つつもりもない。
r550|narrator|アカリが雪女の前へ湯飲みを置いた。自分の分は持っていなかった。何を言えばよいか決められないまま、盆を抱えて立っていた。
r551|e050|先ほどは、ひどいことを申しました。
r552|akari|うん。……忘れられないと思う。あの人を守れなかったらって、私も毎日考えるから。
r553|narrator|雪女は湯飲みに触れた。湯気は凍らなかった。アカリはそれを見届けてから、ようやくあなたの横へ座った。
r554|e050|この国の冬は長いのです。帰りを待つ間に、何をしたらよいのかも、わからなくなっていました。
r555|player|まず、あの門を二度と凍らせるな。春になったら、今度はお前が里へ来ればいい。
r556|narrator|あなたが差し出したのは、里までの道を描いた紙だった。雪女は、そこに指を置いた。
r557|e050|……山を、二つ越えるのですね。
r558|suzune|橋もある。古いやつだから、あんまり厚く凍らせるなよ。見たいなら、あたしが案内する。
r559|narrator|夜、奥の間に食事が運ばれた。あなたの一段下へ並べられた四つの膳を、スズネが持ち上げた。雪女は何も言わず、自分の膳も横へ寄せた。
r560|e050|……お茶を、いただけますか。
r561|narrator|あなたが湯飲みを渡すと、雪女はその手を見た。三百年前より小さな手だった。掴もうとせず、両手で湯飲みだけを受け取る。
r562|e050|お帰りなさい。……今夜くらいは、そう言わせてください。
`),
  ep('main-06', 'main', '第六話　借り物の力、その先へ', '焔谷国', 'volcano', ['suzune', 'e060'], `
@moment|enter
@scene|volcano|焔谷国・紅峰地方・鍛冶場への道|suzune,e060
@bgm|journey
r201|narrator|スズネの槌の亀裂が、柄の付け根まで伸びていた。頼れる炉を求めて紅峰へ入ると、工房の戸は閉じ、輪入道が黒い鎧の兵を待ち構えさせていた。
r201a|e060|それを振るな。次に端へ当てれば、破片が自分の顔へ飛ぶぞ。
r202|suzune|直してほしい。代金は払う。元の重さは、できれば変えたくない。
r202a|narrator|輪入道は手を伸ばしかけて、兵の黒い胸当てへ目をやった。スズネは槌を背へ戻さず、返事を待った。
r203|e060|柄が長すぎるな。よく折らずに使ってきた。……置いていけ。槌も、持ち主もだ。
r203a|suzune|直す間ここへ残れってことか？　……おい、なんで出口を閉める。
@bgm|tense
r204|player|その鎧、俺たちが来る前から用意していたな。誰に頼まれた。
r204a|e060|王印が光った日に、酒呑から注文が来た。眷属の妖力を鈍らせる鎧だ。生きた使い手を添えれば、炉へ回す炭の山を一つくれると。
r205|e060|あの注文は、始まりにすぎん。今はわし自身が使う。眷属を封じる鎧があれば、王の旗にも従わずに済む。お前たちの力を調べて、完成させる。
r205a|narrator|黒い胸当てが迫り、四人の印が薄れた。スズネは閉まった戸を見て、輪入道の炉へ目を戻した。修理を頼むために下げた頭を、ゆっくり上げる。
r206|suzune|あたしの槌を見て、直す前に値札をつけたのか。
r206a|player|下がれ、スズネ。……怒るなとは言わん。戸は、あとでまとめて壊す。
@moment|fort
@scene|volcano|焔谷国・紅峰地方・紅峰砦の炉前|suzune,e060
@bgm|warm
r207|narrator|砦の床に黒い鎧が並んだ。胸当ては無傷だったが、膝の継ぎ目と留め具が外れ、兵たちは立っていられなくなっていた。
r207a|narrator|輪入道は壊れた留め具を一つ拾った。スズネの槌には、亀裂に沿って布が何重にも巻かれていた。
r208|e060|妖力を止められても、同じ場所へ当て続けたか。ずいぶん面倒な壊し方をする。
r208a|suzune|直すときに困る場所を叩いた。胸を殴るより、こっちのほうがよく知ってる。
r209|e060|わしの鎧も、お前の槌も、あと少しだったな。打ち合う時間を間違えれば、そちらが先に壊れた。
r209a|suzune|ああ。だから、次の城までに補強する。割れ目を隠して振るよりは、ましだろ。
r210|player|道具を取り上げれば終わりだと思ったか。こいつは素手でも、釘の曲がりを直すぞ。
r210a|e060|わしも、初めは素手だったわ。炉を焚く炭を買うために、何本の刀を打ったと思う。城の大炉だけは、渡さん。
r211|narrator|輪入道は兵を城へ退かせた。最後に、台の上へ鉄の帯を一本置く。スズネが手を出すと、値を書いた札を添えた。
r211a|e060|持っていけ。つけるなら、その幅だ。……折れるとわかっている槌が、目の前にあるのは目障りだ。
r212|suzune|受け取る。でも、さっきのことは、それで帳消しにしないからな。
r212a|narrator|スズネは鉄帯の幅を測った。あなたが台を押さえ、三人が道具を揃えると、野営の前に短い修理の時間が始まった。
@moment|castle
@scene|volcano|焔谷国・焔谷地方・焔谷城の大炉|suzune,e060
@bgm|tense
r213|narrator|大炉の前で輪入道が待っていた。黒い鎧の留め具には新しい覆いが付いている。スズネはそれを見て、手を止めずに槌を確かめた。
r213a|e060|炉の火を消す者は、生かして帰さん。この火を起こすために、わしは百年かけた。
r214|suzune|消しに来たんじゃねえ！　あんたが直してくれると思って、ここまで持ってきたんだぞ！
r214a|narrator|輪入道はスズネの槌へ目を落とした。砦で置いた鉄帯が、割れ目をきつく抱いていた。返事の代わりに、背の車輪が火を噴いた。
r215|e060|よい。では、その手がどこまで持つかだ。頭だけ丈夫にしても、使い手が折れれば終わりだぞ。
@bgm|resolve
r215a|akari|スズネ、一度戻る場所を決めよう。あの柱までなら、火を避けられる。
r216|yuzuha|覆いが増えた分、鎧は動きにくいはずです。振り返らせますから、正面で押し合わないで。
r216a|kazamaru|煙は上へ流す。こっちの合図まで見えなくなったら、作戦どころじゃねえし。
r217|player|スズネ、交代を呼んだら一度離れろ。道具も手も、今日で使い終わりじゃない。
r217a|suzune|わかってる。でも最初だけは、自分で試したい。直したところが、ちゃんと使えるか。
r218|e060|なら、持ってこい。わしの前まで。その槌を落とさずに来られるならな。
r218a|narrator|スズネが一歩踏み込む。握りの布が軋んだ。あなたはいつもより遅い振り上げを見て、交代の合図を出す位置を決めた。
@moment|clear
@scene|volcano|焔谷国・焔谷地方・焔谷城の炉前|suzune,e060
@bgm|warm
r219|narrator|輪入道は槌を下ろし、座り込んだスズネの手から大槌を受け取った。補強は残っていたが、握りの布には血が滲んでいた。
r219a|e060|まず手を洗え。傷のあるまま道具を握らせたら、今度はわしの仕事に響く。
r220|suzune|槌だけ返されても困るぞ。元より重くして、丈夫になったなんて言うなよ。
r220a|e060|使い手の腕を見てから決める。明日、炉へ来い。今日は火を入れて、割れたところを調べる。
r221|player|炭の山へは俺の名で話をつける。だが、人を売った代金としてではない。止めていた村の農具も、お前の炉で直せ。
r221a|e060|……わかった。酒呑には、受け取れなかったと書く。あの注文を受けたのは、わしだ。名前を消すつもりもない。
r222|yuzuha|手紙の前に、表の戸を開けてください。鎌を預けたまま、冬を越した人が待っています。
r222a|narrator|輪入道は戸のほうを見た。並ぶ鍬と鎌には、煤が厚く積もっていた。大きな手で一つ拭い、炉の傍へ移した。
r223|suzune|……あたしのは、その次でいい。
r223a|narrator|言ってから、スズネはアカリを見た。アカリはため息をついたが、傷のある手へ布を掛けるだけにした。
r224|akari|じゃあ、今日は寝よう。明日の朝、炉へ来るまでに、その手を何とかしなきゃ。
r224a|narrator|スズネは槌を輪入道へ預けた。戸口で振り返ると、煤を払われた鎌の隣へ、大きな槌がそっと寝かされるところだった。
`),
  ep('main-07', 'main', '第七話　終わらない当直', '月影国', 'grave', ['yuzuha', 'e070'], `
@moment|enter
@scene|grave|月影国・霧原地方・古い街道|yuzuha,e070
@bgm|mystery
r201|narrator|月影を通る荷が途絶えた。確かめに来たあなたたちの前を、古い軍勢が往復していた。荷車の轍は霧の手前で終わり、先頭の怨霊将軍が王印へ刀を向けた。
r201a|akari|あの鐘、ずっと鳴ってる。村の人、夜は窓も開けられないって。
r202|e070|その印を返せ。王の帰還を騙り、街道へ入った者は逃がさぬ。
r202a|player|俺だ。戻った。まず兵を止めろ。荷を運ぶ者まで追い返していると聞いた。
r203|e070|王は、三百年戻られていない。印を盗んだ者なら、今までにも来た。
r203a|narrator|あなたは将軍の旗を見た。端の縫い目まで覚えているのに、最後にその旗を見た日だけが思い出せなかった。
r204|player|何を話せば信じる。お前を知っていることなら、いくらでも言える。
r204a|e070|ならば、なぜ交代を寄越さなかった。誰一人として、我らに持ち場を離れよとは言わなかったぞ。
r205|yuzuha|……今、ここで答えられることではなさそうです。ですが、街道を塞いだまま帰るわけにもいきません。
r205a|suzune|荷車は脇へ寄せよう。あいつら、通り道にある物を避けてない。
r206|narrator|将軍が進むと、倒れていた旗まで起き上がった。兵たちは壊れた橋の先へも、同じ歩幅で進もうとしていた。
r206a|player|橋へ行かせるな。霧原の砦を押さえる。あの鐘が命令を伝えているなら、先に止めるぞ。
@moment|fort
@scene|grave|月影国・霧原地方・霧原砦の当直所|yuzuha,e070
r207|narrator|砦を押さえたあとも、将軍は鐘へ手を伸ばした。あなたは脇の小鐘を拾い、間をあけて三つ鳴らした。将軍の手が、初めて止まった。
r207a|narrator|兵たちの列が崩れた。何人かは周りを見回し、自分がいつからそこに立っていたのか、確かめるように足元を見た。
r208|e070|……その合図を、どこで知った。
r208a|player|俺が決めた。古い合図は複雑すぎて、疲れた兵がよく間違えたからな。
r209|e070|三つ鳴れば、交代。軍旗を戻して、次の者へ道を渡す。
r209a|narrator|将軍はそこまで言って黙った。当直表の最後に、赤い染みがあった。そこだけは紙が破れても繕われ、三百年前の日付が読めた。
r210|e070|城の底から火が上がった夜だ。王は西門を開けよと命じた。出ていったのは民と兵だけだった。王は、来なかった。
r210a|yuzuha|それで、門からここまでを、何度も……。
r211|e070|煙の中では、すぐそばの者も見えなくなる。王も道を見失ったのだと思った。旗を立てていれば、戻ってこられると。
r211a|player|もう見つけた。ここにいる。城へ戻ったら、軍旗を下ろせるか。
r212|e070|……城で待つ。今は、それしか約束できぬ。あの旗を下ろしたことが、ないのだ。
r212a|narrator|将軍が退くと、鐘の音は遠くなった。ユズハは破れた当直表を一枚拾い、あなたへ渡さず、自分の文箱へ納めた。
@moment|castle
@scene|grave|月影国・月影地方・月影城の本陣|yuzuha,e070
@bgm|tense
r213|narrator|本陣に着くと、将軍は軍旗の前に立っていた。旗へ近づくたび、その身体が霧に崩れては戻る。刀の音だけが続いていた。
r213a|e070|下ろそうとはした。だが、ここで待つ間に積もったものが、腕を止める。
r214|e070|王なら、なぜ帰らなかった。城を守れと命じたまま、何をしていた。
r214a|narrator|あなたは答えを探した。幼い頃の里の記憶と、昔の城の記憶。その間は、まだ空いていた。
r215|player|そこから先が思い出せん。扉と火と……『すぐ戻る』と言った、自分の声だけだ。
r215a|e070|その声は、この城の印からも聞こえた。だから我らは待った。三百年、その一言だけを頼りにな。
r216|narrator|弁解になる言葉ばかりが浮かび、あなたは口を閉じた。背後でアカリが息を呑む。将軍は軍旗を握り、もう片方の手であなたへ刀を向けた。
@bgm|resolve
r216a|akari|……ここで一人で謝ろうとしないで。私たちまで帰せって言わないで。
r217|yuzuha|あの旗の紐が、軍勢へ伸びています。将軍を倒すだけでは、別の兵が同じ場所へ立ってしまう。
r217a|kazamaru|なら、旗まで道を開ける。声が通らないなら、おれの羽を見て動けよ。
r218|player|全員で軍旗へ行く。将軍、俺たちを見失うな。旗を下ろしたら、次の命令を聞いてもらう。
r218a|e070|来い。三百年、この場所を空けられなかった。それを終わらせるなら、最後まで立ってみせろ。
@moment|clear
@scene|grave|月影国・月影地方・月影城の夜明け|yuzuha,e070
@bgm|warm
r219|narrator|軍旗が下り、鐘が止まった。兵の列から一人ずつ膝をつく。誰も次の列を作らないのを確かめてから、あなたは声をかけた。
r219a|player|当直は終わりだ。この街道を、昔の命令で守る必要はもうない。
r220|player|持ち場を離れていい。帰る家を探す者には道案内をつける。見つからない者は、まずここで休め。
r220a|narrator|命令を聞き終えた兵が、刀を鞘へ戻そうとして手間取った。長く握りすぎた指を、スズネが横から一本ずつ開いてやった。
r221|e070|眠っても、よいのか。起きたときに、また誰もいなくなることは。
r221a|yuzuha|私、入口にいます。そこから見えますか。灯りを消さずにいますから。
r222|player|今夜はここにいる。明日、目が覚めたら、もう一度話そう。
r222a|narrator|将軍は入口の灯りを見た。まぶたを下ろしては持ち上げ、ユズハがいるのを確かめる。その間隔が少しずつ長くなった。
r223|akari|あんたも座って。見張りを全部引き受けるつもりの顔してる。
r223a|player|最初の当番だけだ。次はカザマル、その次は……。
r224|kazamaru|最初はおれ。お前、今その顔で座ってたら、朝まで同じこと考えるだろ。
r224a|narrator|あなたは言い返せず、壁へ座った。アカリが横に来た。話しかけることもなく、冷えた指の上へ自分の手を重ねた。
`),
  ep('main-08', 'main', '第八話　落ちこぼれ、空を奪う', '鳴神国', 'mountain', ['kazamaru', 'r08'], `
@moment|enter
@scene|mountain|鳴神国・雲居地方・天狗の山門|kazamaru,r08
@bgm|journey
r201|narrator|鳴神の天狗は空路を閉じ、他国の荷を山から追い返していた。山門で、カザマルが団扇の柄を握り直す。生まれ故郷だった。
r201a|narrator|白髪の烏天狗の長が門の上へ降りた。年老いても、羽ばたきに迷いがない。カザマルはいつもの軽口を飲み込んだ。
r202|r08|山風にも乗れなかった雛が、魔王の旗を掲げて帰るか。
r202a|kazamaru|まだ雛って呼ぶのかよ。門の高さ、昔はもっとあった気がしたんだけどな。
r203|r08|背が伸びたくらいで道が開くと思うな。この山を知らぬ王の兵を、好きには飛ばせん。
r203a|kazamaru|薬まで引き返させることはねえだろ。おれの顔なら知ってる。今日は道を開けてもらいに来た。
@bgm|tense
r204|r08|その翼に流れているのは、誰の力だ。借りた力が消えても、同じことを言えるのか。
r204a|narrator|カザマルは団扇を握り込んだ。あなたが横へ並ぶと、止めるように片手を出した。
r205|player|ここは、こいつに任せる。通る道も、勝ち方も。山を知っているのはカザマルだ。
r205a|r08|人を乗せて飛べるようになったからと、山を売るな。下の者を通せば、明日は奴らの旗が頂へ立つ。
r206|kazamaru|じゃあ、立てるよ。おれが立てて、下から来たやつを迎える。薬まで落とすような決まりなら、ここで終わらせる。
r206a|narrator|烏天狗の長が山の上へ戻る。カザマルは追いかけず、門の横を流れる細い風へ手をかざした。それから、谷へ下る道を選んだ。
@moment|fort
@scene|mountain|鳴神国・雲居地方・雲居砦の谷底|kazamaru,akari
@bgm|warm
r207|narrator|雲居の砦が落ちた。烏天狗の長は上空へ退いたが、カザマルは追わずに谷底を見ていた。砦の旗が、風を受けて下へはためく。
r207a|player|追えば届いたんじゃないか。珍しく、相手の顔を見に行かなかったな。
r208|kazamaru|見たかったよ。悔しそうな顔、せっかくしたのに。……でも、あの風に入ったらまた落ちる。
r208a|akari|知ってる風なの？　さっきから、葉っぱが下から上へ戻ってくるけど。
r209|kazamaru|上の風に弾かれたあと、谷の底から吹き返すんだ。昔はそれに拾われて、やっと帰ってた。
r209a|narrator|カザマルが小石を一つ投げた。岩へ当たって落ちる途中、軽い葉だけが別の向きへ跳ね上がった。
r210|player|烏天狗の長は上から来る。お前は、その吹き返しで下から出るつもりか。
r210a|kazamaru|ああ。ただ、上へ出た瞬間は後ろが見えねえ。そこに兵がいたら、前と同じだ。
r211|suzune|じゃあ、あたしらが下を押さえる。この岩棚なら、足を置けるぞ。
r211a|yuzuha|移る順番も決めましょう。二人同時に乗ると、端が崩れそうです。
r212|narrator|カザマルは窪みを一つずつ指した。落ちたあと、必死につかまった場所だった。今日は、四人が立つ位置として地図に残した。
r212a|kazamaru|ここで待っててくれ。……落ちるために使うんじゃねえからな。上まで出るためだ。
@moment|castle
@scene|mountain|鳴神国・鳴神地方・鳴神城の断崖|kazamaru,r08
@bgm|tense
r213|narrator|烏天狗の長が城前へ風の壁を作った。細い岩の上で片足を上げ、カザマルへ団扇を向ける。昔の稽古と同じ構えだった。
r213a|r08|上がってこい。山の頂を、谷底から眺めるだけで終わる気か。
r214|kazamaru|そこへまっすぐ上がるたび、あんたに落とされた。おれの羽じゃ、風を受けきれなかったからな。
r214a|r08|覚えているなら、羽ばたき方を変えろ。力任せに上がるのは、雛でもできる。
r215|kazamaru|変えたよ。あんたの見てないところで。今日は、そっちが見つけろ。
r215a|narrator|カザマルは崖へ身を投げた。アカリが一歩前へ出かけて止まる。谷の中ほどで、団扇が一度だけ白く光った。
@bgm|resolve
r216|player|合図だ。スズネ、最初の岩へ。ユズハはその横。アカリ、光を隠すなよ。
r216a|akari|わかってる。こっちからは、ちゃんと見えてる！
r217|narrator|四人が足場へ散った。烏天狗の長が谷へ風を叩き込むと、カザマルは逆らわず、さらに下へ潜った。吹き返しが来るまで、翼を畳んで待つ。
r217a|r08|……下の者まで連れてきたか。お前がそこを通るために。
r218|kazamaru|おれだけ戻れたって、城まで来られねえだろ。行くぞ、今度は上で会うからな！
r218a|narrator|団扇の光が、崖の縁より高く上がった。あなたは敵兵の動きを見て、風に負けない大きさで次の合図を叫んだ。
@moment|clear
@scene|mountain|鳴神国・鳴神地方・鳴神城の山門|kazamaru,r08
@bgm|warm
r219|narrator|烏天狗の長が傷んだ羽を抜き、団扇を下ろした。カザマルの周りへ若い天狗たちが集まる。昔、落ちる彼を笑っていた者もいた。
r219a|narrator|一人が谷のほうを指し、飛び方を尋ねた。カザマルは答えかけてから、まず荷を運ぶ道を開けろと山門を示した。
r220|r08|一対一ではなかったな。わしを越えるのに、四人も借りた。
r220a|kazamaru|あんたも城も兵も使ったろ。国を取りに来たんだ。稽古の点を貰いに来たんじゃねえよ。
r221|narrator|烏天狗の長はしばらく黙り、やがて門の鎖を外した。上空で待っていた荷運びの天狗たちが、ようやく翼を広げた。
r221a|r08|この山に残れ。道を開いたなら、その先を使う者も率いていけ。
r222|kazamaru|任せっぱなしにはしねえ。でも、おれだけここへ残るつもりもない。あちこち飛んで、道を繋ぐほうが向いてる。
r222a|player|山の決まりはお前たちで教えてやれ。俺の兵にも覚えさせる。勝ったからと、危ない風まで無視はできん。
r223|r08|では、最初の便を見ていけ。今朝の風を、若い者たちはまだ知らぬ。
r223a|narrator|カザマルは最初の荷を受け取った。幼い天狗が谷へ落ちかけ、昔彼を笑った者が吹き出す。カザマルの団扇が、その鼻先でぴたりと止まった。
r224|kazamaru|笑ってねえで、下へ行け。拾う場所を知ってるやつが、一人もいなくてどうすんだ。
r224a|narrator|若い天狗が慌てて谷へ降りた。烏天狗の長は白い眉を寄せ、それから小さく頷いた。カザマルは次の荷を肩へ掛けた。
`),
  ep('main-09', 'main', '第九話　茨木童子と、六つの椀', '荒磯国', 'oni', ['suzune', 'e090'], `
@moment|enter
@scene|oni|荒磯国・鬼岳地方・黒い浜|suzune,e090
@bgm|journey
r301|narrator|青葉へ向かう米船が、荒磯の港へ引かれた。浜へ来ると、茨木童子がその米を城へ運ばせているところだった。
r302|e090|道を空けな。こっちは冬まで食う米が足りないんだ。話なら、降ろしてから聞くさ。
r303|suzune|聞く前に降ろすなよ。それ、うちの村のやつも待ってるんだぞ。
r304|narrator|茨木はスズネの大槌を見ると、懐から古い手紙を出した。酒呑童子の印がある。日付は、王印が目覚めた日だった。
r305|e090|都から来た誘いさ。魔王から離れるなら、四人に一国ずつくれてやる、とね。俺も一度、誘ってみたくなった。
r306|suzune|一人ずつ別の国？　飯を一緒に食えなくなるだろ。
r307|e090|それが最初かい。城も兵も、自分のものになるんだよ。
r308|suzune|あんたの兵が食う米はいる。うちの村の米もいる。あたしだけ城を貰えばいいって話じゃ、帰れないだろ。
@bgm|tense
r309|player|船を返せ。代わりに港を開けば、米を買う道は繋げる。だが、俺の国の船だけ見逃すという話なら断る。
r310|e090|買う銭のない者は、指をくわえてろって？　俺は、こいつらを食わせると約束したんだ。続きは砦でつけよう。
r311|narrator|茨木は手紙を畳んだ。最後の舟が岸を離れたのを確かめると、鉄棒を肩へ上げ、山の砦を指す。
r312|suzune|なら、城から米ごと持って帰る。あんたの兵の分も、ちゃんと数えてな。
@moment|fort
@scene|oni|荒磯国・鬼岳地方・鬼岳砦の広場|suzune,e090
r313|narrator|砦の旗が落ちた直後、広場の石床が沈んだ。退こうとしていた若い鬼の足が、裂け目へ吸い込まれる。
r314|suzune|動くな！　そこ、下が空いてる！
r315|narrator|スズネは槌を放り出し、鬼の襟をつかんだ。止まったと思った石が、彼女の膝の下でも割れた。
r317|suzune|離したらこいつが落ちるだろ！　見てないで、手を貸せ！
r318|narrator|茨木が鉄棒を横たえ、両端を無事な石へ渡した。そこへ腹を伏せ、スズネの腕をつかむ。二人の足元で土が崩れ続けた。
r321|narrator|若い鬼の足が戻った。スズネが先に彼を押し出し、最後に自分の肘を鉄棒へ掛ける。茨木は彼女が立つまで腕を放さなかった。
@bgm|warm
r322|e090|敵の兵だよ。腕一本持っていかれても、おかしくなかった。
r323|suzune|武器を捨てて逃げてるやつだろ。あたしが放したら、あとでここを通るたび思い出す。
r325|akari|スズネ、腕を見せて。今、巻いておかないと腫れるよ。
r328|narrator|茨木は落ちていた槌を拾い、スズネへ差し出した。重さを確かめるように、受け渡す前に一度だけ上下させた。
r329|e090|こんな重心で振るのかい。さっき、二発目が早かったのはそのせいか。
r330|suzune|振り切らないで戻す。持ち手をこっちへずらせば――いや、今教えることじゃないな。
r331|e090|遅い。半分わかった。
r335|suzune|穴の周りに杭を打っとけ。今夜の見張りが知らずに通るぞ。
r336|e090|砦はくれてやる。だが、荒磯城は渡さないよ。港の連中の分まで、そこで止める。
r339|e090|槌持ち。兵を助けた借りは返す。お前たちの船だけなら、出してやる。それで引きな。
r340|suzune|それじゃ、次の船が空になるだろ。……腕は、助かった。そこは礼を言う。でも、城へ行く。
@moment|castle
@scene|oni|荒磯国・荒磯地方・荒磯城の大門|suzune,e090
@bgm|tense
r341|narrator|荒磯城の裏手では、朝の舟が荷を下ろしていた。茨木は大門の前へ鉄棒を立て、最後の荷車が内側へ入るまで待った。
r342|e090|兵を助けた礼は言うよ。だが、国はそれだけじゃ渡せない。わかってるね。
r343|suzune|わかってる。床の穴も、礼を言われて塞がるわけじゃないしな。
r344|e090|よその船に手を出すなって話は呑めない。俺の兵を、よその米蔵の機嫌ひとつで腹を空かせる身にはしないよ。
r345|player|お前が船を襲うから、荷主は護衛を増やす。その金が米の値に乗る。これを続けて、いつ腹いっぱい食えるんだ。
r346|e090|わかってるさ！　だが、来年まで待てないやつが、この門の中にいるんだ！
r347|yuzuha|……声を落としてください。兵が聞いています。頼っている人がそんな顔をしたら、逃げたいとも言えなくなります。
r350|player|米は取り返す。だが、明日ここで飢える者を出すつもりはない。降りた者には分ける。お前の席も一つ空けておく。
r351|e090|勝つ前から、俺の椀の数まで決めるんじゃないよ。……言ったからには、忘れるな。
@bgm|resolve
r353|suzune|いつも片手で振るのか、それ。砦じゃ、ずいぶん軽そうに持ってた。
r354|e090|両手なら、もっと飛ぶよ。試すかい。
r355|suzune|あたしも両手でいく。後ろのやつ、もう少し下がらせろよ。
r356|narrator|茨木が笑った。兵を両側へ下げ、自分の足で間合いを測る。スズネも同じ距離だけ下がった。
r357|akari|二人とも、楽しそうにしないで。スズネ、右の腕はまだ巻いたばかりだからね。
r363|e090|この城の後ろに、俺が守ってきた家がある。ここを退くのは、負けたときだけさ。
r364|player|スズネ、最初の一発は任せる。俺はあいつの兵を見る。ほかの三人も、決めた位置へ。
r365|suzune|ああ。茨木、いくぞ。せっかく来たんだ、あたしの名前も覚えて帰れよ！
r366|narrator|大槌が上がった。あなたは四人の背を確かめ、門の脇に空けた負傷者の道を、最後にもう一度見た。
@moment|clear
@scene|oni|荒磯国・荒磯地方・荒磯城の城壁|suzune,e090
@bgm|warm
r367|narrator|茨木が鉄棒を下ろした。刃を向けたままの兵へ、手のひらを見せて止める。息を整えてから、旗を下ろすよう命じた。
r368|e090|負けた。怪我人を運ぶまで、門は開けておきな。俺も、この棒から手を離す。
r369|player|こちらも武器を下げろ。アカリは手当てへ。カザマル、城の外へ勝負がついたと知らせてくれ。
r370|narrator|スズネは槌を抱え直し、担架の置かれた場所へ向かった。三歩目で膝が折れ、茨木に肩をつかまれた。
r371|e090|お前は座りな。さっきから膝が笑ってる。
r375|akari|はい、水。まず飲んで。それから腕を見せる順番を決めて。二人同時には巻けないから。
r379|yuzuha|都からの手紙は、どうしましょう。返事を届ける便なら、明日出せるそうです。
r381|suzune|断る。国を貰ったら、その次はあたしらの国同士で戦えってなるのか？
r383|player|なら、こっちから条件をつけよう。四人を欲しければ、五人まとめて迎えろと。
r384|narrator|茨木は笑い、手紙の裏を使おうとした。濡れた指からユズハが紙を取り上げる。アカリはあなたの袖をつかんだまま、頷いた。
r384v1|akari|私はここにいる。今も、返事が届いたあとも。……ちゃんと四人ぶん、断って。
r385|e090|『四人とも断る。国を任せたいなら、五人の前で話せ』。これでいいかい。
r386|kazamaru|ああ、それでいい。おれだけどっかの城へやられても、朝、起こすやつがいねえし。
@scene|oni|荒磯国・荒磯地方・荒磯城の炊事場|suzune,e090
@bgm|daily
r391|narrator|城の蔵を開けると、奪った米袋が出てきた。半分を船へ戻し、残りはあなたの名で買い取って炊かせた。銭袋は空になり、六つの椀から湯気が立った。
r391a|yuzuha|王様。今月のお小遣い、今ので終わりです。
r391b|player|……王に、小遣いがあるのか。
r391c|yuzuha|帳面をつけているのは、私ですから。
r392|suzune|五人分、並んで座れるか。離れてると、王様が食べたふりして立つから。
r393|player|今日は立たない。……一番に座るのも、王の務めだ。
r394|e090|俺の分まで、本当に出したのかい。……次は、こっちが払うよ。港の銭が入るまで、少し待ちな。
r402|narrator|茨木はしばらく椀を抱えていた。やがて黙って食べ始め、二杯目をスズネと同時に差し出した。
`),
  ep('main-10', 'main', '第十話　魔王と四天王', '幽京国', 'capital', ['yuzuha', 'e100'], `
@moment|enter
@scene|capital|幽京国・常闇地方・羅城門|yuzuha,e100
@bgm|journey
r501|narrator|十国を繋ぐ道の多くは、百鬼の都へ集まる。その門にはまだ酒呑童子の旗があった。あなたは里から持ってきた地図を畳み、見覚えのある石段を登った。
r502|narrator|酒呑は杯を置き、門前へ降りた。三百年ぶりの顔だった。あなたが覚えているより、目元だけが険しくなっていた。
r503|e100|小さくなったな。見下ろす側になるとは思わなかった。
r503a|player|……階段の上から言うな。降りてこい。
r503b|narrator|酒呑は一段だけ降りた。それでも、まだ見下ろされていた。
r504|player|お前は変わらん。……俺が消えた夜のことを、知っているか。
@bgm|tense
r505|e100|お前が俺を城から追い出したことならな。玉座を預けると言って、扉を閉めた。戻らなかった理由は、お前に聞きたい。
r506|narrator|言い返せなかった。酒呑は答えを待つのをやめ、あなたの後ろの四人へ目を移す。印を見つけたとき、杯を置いた指が一度だけ曲がった。
r507|e100|また四人も連れたか。強くして、頼らせて、最後はどこへ置いていく。
r508|akari|置いていかれに来たんじゃないよ。ここまで、私たちが連れてきたんだから。
r509|e100|なら聞こう。王がここで死ぬと言ったら、お前たちは止めるか。それとも、お見事でしたと褒めるか。
r510|player|こいつらに答えさせるな。今日ここで死ぬつもりはない。城を取って、里へ帰る。
r511|e100|俺の兵も、ここに生まれた者も、お前の帰りを待っていたわけではない。昔の名だけで渡せる都ではないぞ。
r512|player|だから取りに来た。お前にも、俺の旗の下へ来てもらう。昔の続きができるかどうかは、それから話す。
r513|narrator|酒呑が初めて笑った。愉快そうではなかったが、目を逸らすことはしなかった。門の横を避け、常闇の砦へ続く道を開ける。
r514|e100|来い。あの夜、追い出されたところから先へ、俺も一度進んでおきたい。
@moment|fort
@scene|capital|幽京国・常闇地方・常闇砦|yuzuha,e100
r515|narrator|砦の武器蔵に、焔谷の焼き印がある木箱が並んでいた。中身は、眷属の妖力を鈍らせる黒い鎧。荒磯へ送った『四人に一国ずつ』の誘い状の控えも、同じ棚に綴じてあった。
r518|narrator|酒呑は最後の兵を城へ退かせて戻ってきた。控えを見られても、取り返そうとはしない。
r519|player|鎧と、国の切り売りか。よほど俺を一人にしたかったらしい。
r520|e100|ああ。眷属が横にいる限り、こいつはいつか力を引き戻す。だから離しておきたかった。鎧で封じても、国で釣ってもよかった。
r521|suzune|こっちは知らねえよ、そんな都合。あたしらを売り買いする紙に、名前を書いたのはあんただろ。
r522|e100|そうだ。俺を恨め。だが、この王はお前たちの力を、自分へ戻すこともできるぞ。
r523|narrator|誰もすぐには口を開かなかった。酒呑はあなたの胸へ指を向ける。そこにある印の形を、見ずとも覚えているようだった。
r524|e100|仲間が倒れかけたとき、こいつは自分一人を残そうとする。あの夜もそうだった。俺たちを守ったつもりで、手足を奪って閉め出した。
r525|player|……そんなつもりで、力を渡したんじゃない。
r526|e100|渡したときの話はしていない。取り返すとき、お前が何と言うかの話だ。
r527|narrator|アカリがあなたの袖へ触れ、途中で手を引いた。あなたは呼び止められなかった。酒呑の言葉を、嘘だと断じる記憶がなかった。
r528|e100|本城で待つ。今度こそ、最後まで四人を連れてこい。途中で追い返したら、ここまで開いた門も閉める。
@moment|castle
@scene|capital|幽京国・幽京地方・幽京城の大広間|akari,e100
@bgm|mystery
r529|narrator|大広間の玉座は空いていた。酒呑はその一段下で待っている。床には、奥の扉へ向かって引きずられたような、長い刀傷があった。
r530|player|お前は、座らなかったのか。そこへ。
r531|e100|持ち主が戻ったら、一発殴ってから座らせるつもりだった。三百年も先とは思わなかったがな。
r532|narrator|奥の扉を見た途端、あなたの足が止まった。火の向こうで同じ戸が閉まる。内側にいるのは自分だ。外から、誰かが叩いている。
r533|player|……あの扉の、向こうは。
r534|e100|地下の王印の間だ。お前が出てこなかった場所だよ。最後に何があったかは、十の印が揃えば残響を辿れる。だから俺は、ここの印を捨てられなかった。
r535|narrator|あなたは前へ出た。四人を置いたまま、扉へ手を伸ばす。袖を強く引かれ、振り向くとアカリが睨んでいた。
@bgm|tense
r536|akari|……その顔、やめて。今ここにいるの、昔のあんたを呼び戻すためだけじゃない。
r537|suzune|扉ならあとで開ける。壊してでも開けるから、今はあっちを見ろ。酒呑が刀を抜いてる。
r538|e100|昔の王なら、俺の一太刀は避けた。今のお前には無理だ。四人が倒れたら、力を戻すか。もう一度、昔の身体で立ってみるか。
r539|narrator|喉の奥が乾いた。四人を傷つけずに済むなら、その力が欲しかった。欲しくないふりをするほうが、難しかった。
r540|player|戻したくなると思う。お前たちが倒れたら、たぶん。
@bgm|resolve
r541|kazamaru|じゃあ、先に言っとく。勝手にやったら殴る。羽が動かなくなっても、お前のところまでは歩くからな。
r542|yuzuha|私たちを逃がす道はあります。あなたを置いていく道は、調べていません。今から命じられても、困ります。
r543|narrator|スズネが槌の石突で床を叩いた。戦う前の合図だった。四人は、あなたの言葉が立派になるのを待ってはいなかった。
r544|player|……来い、酒呑。こいつらの邪魔にならない場所で、俺も戦う。
r545|e100|よく言った。今度は、苦しくなってから聞かせろ。
r546|narrator|刀が上がる。あなたは奥の扉に背を向け、目の前で呼吸を揃える四人を見た。
@moment|clear
@scene|capital|幽京国・幽京地方・幽京城の玉座前|akari,e100
r547|narrator|勝敗を分けたのは、一瞬だった。酒呑の刀でスズネの槌が跳ね上がり、アカリの灯が、一つ消えた。
r548|narrator|あなたは四人の印へ手を伸ばしかけた。引けば、力は戻る。四人は倒れても、自分だけはまだ立てる。その手を、スズネがつかんだ。
r549|suzune|まだ、離すな！　あたし、手が滑っただけだ！
r550|narrator|あなたは戻しかけた力を、そのまま送り続けた。腿が震えた。アカリの灯が戻り、酒呑の目がほんの一瞬、細くなる。
r551|kazamaru|上だ！　見ろよ、ずっと得意だったんだろ、上から見下ろすの！
r552|narrator|突風に羽織が煽られ、酒呑が顔を上げる。その足元で、ユズハが刀の向きを変えた。スズネは槌を持ち直すため、あなたの手を離した。
r553|player|行け！
r554|narrator|声が裏返った。気の利いた命令にはならなかった。だがスズネは笑い、振り返らずに踏み込んだ。
@bgm|warm
r555|narrator|気づくと、酒呑の刀は玉座の下へ転がっていた。アカリは倒れたあなたの背を起こし、何度も名前を呼んでいた。
r556|akari|返事して。聞こえてるなら、手、握って。……お願いだから。
r557|player|聞こえてる。声、近すぎる。
r558|narrator|アカリの額があなたの肩へ落ちた。痛かった。言えば怒られる気がして、そこだけは黙っていた。
r559|e100|……最後まで、取り返さなかったな。
r560|player|取り返す暇がなかった。こいつら、俺が格好つける前に、次のことを始める。
r561|narrator|酒呑は笑おうとして咳き込んだ。杯を探したが、スズネに水を差し出され、諦めてそれを飲んだ。
r562|e100|四天王を連れてきたなら、先に言え。昔のお前一人を相手にしていたつもりで、負けた。
r563|akari|し……てんのう？
r564|player|ああ。俺の四天王だ。
r565|kazamaru|勝手に決めんなよ。……いや、別に、嫌とは言ってねえけど。
r566|yuzuha|式は立っていられる日にしてください。今、ひざまずくと、四人とも起き上がれません。
r567|player|十国の旗が揃った日にしよう。それまでは、この呼び方だけ先に使わせてくれ。
r568|narrator|酒呑が腰から国の印を外した。あなたは受け取っても、奥の扉へ走らなかった。四人の息が整うのを、座ったまま待っていた。
`),
  ep('main-11', 'main', '終章　王様と四天王の朝ごはん', '幽京国・王城／帰還と即位', 'capital', ['akari', 'suzune'], `
@moment|ending
@scene|capital|幽京国・王印の間／十国の印が揃った夜|akari,e100
@bgm|mystery
r501|narrator|十国の印が、地下の石卓へ揃った。最後の一つを置いても、勝利の鐘は鳴らない。代わりに、奥の扉の向こうで戸を叩く音がした。
r502|e100|あの夜から、ときどき聞こえる。中で誰かが生きていると思って、何度ここを掘ったかわからん。
r503|narrator|酒呑の足元には、折れた刃が何本も埋まっていた。あなたが印へ触れると、指先から熱が上がる。石の中へ置き忘れたものが、ようやく持ち主を見つけた。
r504|player|……違う。中から叩いているんじゃない。外から叩いた音だ。あの夜、お前が叩いた音だ。
r505|e100|……印が、覚えていたのか。俺がいくら叩いても、お前は開けなかったな。
r506|narrator|四人の印が熱くなった。あなたは思わず手を離しかけ、アカリの指に触れた。握り直すと、灯りが消えた。
@scene|capital|三百年前・王城の地下|
r507|narrator|闇の底に、昔の自分が立っていた。都の下の地脈が裂け、火が石壁を舐めている。
r508|narrator|裂け目を塞ぐ力が足りない。だから王は、眷属へ与えた力を、一度に自分へ引き戻した。
r509|narrator|印の消えた酒呑を、扉の外へ押し出す。手を貸せと叫ばれても、戸を閉めた。一人なら、誰も死なせずに済む。そう思った。
r510|player|すぐ戻る。玉座は預けた。
r511|narrator|王印に触れたまま言った最後の言葉は、十の城へ届いた。戻る当てがあったのではない。そう言えば、皆が逃げると思ったからだ。
@bgm|sad
r512|narrator|裂け目は塞がり、火は都へ届かなかった。代わりに、受け止めきれない力が王の身体を内側から焼いた。
r513|narrator|あなたは、その痛みを思い出した。指も喉も失って、もう帰れないとわかった瞬間の、みっともない恐ろしさを。
r514|player|まだ、帰ると……。
r515|narrator|声にはならなかった。王印がその願いと、消えかけた魂を受け取った。城から城へ流れ、最後に対となる紅葉の社の石へ辿り着いた。
@scene|village|紅葉の里・社／長い眠りの記憶|
r517|narrator|長い年月、里のわずかな妖気に養われた魂は、ある春、記憶を持たない赤子として石から零れた。
r518|narrator|石段で泣く赤子を、誰かが抱き上げた。あなたは、その腕の温かさを覚えていた。
r519|narrator|それからのことは、よく知っている。屋根から落ち、四人と出会い、何度も名前を呼ばれた。
@scene|capital|幽京国・王印の間／現在|akari,e100
r520|narrator|灯りが戻った。あなたは石卓へ額を押しつけていた。アカリが握っていた指を一本ずつ解き、同じ所を擦っている。強く握りすぎたらしい。
r521|akari|……見えた。最後まで。あんなの、一人で帰ってこられるわけないじゃない。
r522|player|帰れると思っていた。少なくとも、扉を閉めるまでは。
r523|e100|なら、閉める前に言え。怖かったと。俺たちの前でくらい、言えたはずだ。
r524|narrator|酒呑の声が震えた。怒鳴られたほうが、まだ返事ができた。あなたは古い刀傷へ目を落とし、それから顔を上げた。
r525|player|すまなかった。助けたつもりで、お前たちに三百年、あの扉を叩かせた。
r526|narrator|酒呑は答えなかった。掌を石卓へ置き、折れた刃の一つを拾った。まだ捨てられないようで、腰の袋へしまった。
@scene|capital|幽京国・王印の間／現在|suzune,yuzuha
@bgm|warm
r527|suzune|……最後に怖がったの、お前でよかった。
r528|player|何だ、それは。
r529|suzune|王様らしく諦めてたら、里まで戻れなかったんだろ。あたしら、お前のこと知らないままだった。
r530|narrator|ユズハが、あなたの手から印を受け取った。十の灯はもう消えていた。残っていた最後の記憶を返し、石はただ冷たく重かった。
r531|yuzuha|また戻れると思って、同じことをなさらないでくださいね。
r532|player|……もう一度、三百年待たせるのは嫌だ。今の身体で帰りたい。
r533|kazamaru|じゃあ、大事に使えよ。その身体。おれたちが何度、屋根の下で受け止めたと思ってんだ。
r534|narrator|あなたは笑いかけた。喉が詰まり、うまく笑えなかった。四人は急かさなかった。酒呑も出口へ背を向けたまま、待っていた。
@scene|capital|幽京国・王城の廊下|akari,e070
r535|narrator|地下を出ると、怨霊将軍が廊下にいた。あなたは三百年前の最後を、自分の声で話した。途中で何度か言葉が止まったが、最後まで聞いてもらった。
r536|player|道を見失ったんじゃなかった。帰る身体が、もうなかったんだ。……長く、待たせた。
r537|e070|そう、でしたか。ならば、あの夜にもっと遠くまで捜しても、見つからなかったのですね。
r538|narrator|将軍はそれだけ言って、刀を壁へ立てかけた。胸の古い当直札を外そうとしたが紐が切れず、あなたが手を貸した。
r539|akari|上に、お茶を置いてきた。冷めてるかもしれないけど、一緒に飲もう。
r540|narrator|将軍が歩き出した。あなたは扉を閉めずに、そのあとへ続いた。背後からは四人の足音と、酒呑が袋から刃を出し、石卓へ置く音がした。
@scene|capital|幽京国・王城／即位の日|akari,suzune
@bgm|daily
r001|narrator|十国すべてに同じ旗が立って、一月。各国の領主が王城へ集まった。今日は、百鬼の王と、その四天王を迎える日だった。
r002|narrator|控えの間で、カザマルが正装の紐をほどこうとしていた。ユズハに結び直され、諦めたように翼を畳む。
r003|kazamaru|首、締まってねえか。これ。
r004|yuzuha|二回ほどほどいたので、少し強めに。
r005|suzune|アカリ、なんか燃えてる。
r006|akari|えっ。あ、ごめん。袖！　緊張すると火が出る！
r006n2|akari|名前を呼ばれたら前へ、で合ってるよね。そのあと、言葉を間違えたらどうしよう。
r007|player|大丈夫だ。俺も昔、一度やった。
r008|yuzuha|安心する話をしてください。
r008n1|player|噛んだら俺も噛む。そういう作法だったことにする。
r008n2|narrator|四人が一度、あなたの顔を見た。あなたも襟を直そうとして、裏返った紐をスズネに戻された。
r009|narrator|扉が開いた。並ぶ領主たちの中に、化け狸の頭領もいた。目が合うと、彼は慌てて頭を下げる。あのときと同じ尻尾が、椅子からはみ出していた。
@bgm|triumph
r010|player|アカリ。前へ。
r011|narrator|アカリは一歩出て、あなたの顔を見た。いつもの調子で返事をしかけ、唇を結ぶ。
r011n1|akari|……はい。
r012|player|お前の火に、何度も助けられた。これからも、俺と、この国の者を守ってくれ。
r013|akari|……はい。無茶をしたら、止めるところまで。
r013n1|narrator|雪女が袖で口元を隠した。アカリはそちらを見て、一瞬だけいつもの顔で笑った。
r013n2|akari|でも、今日は叱らない。今日くらいは、ちゃんとお祝いしたいから。
r014|narrator|あなたは頷き、次の名を呼んだ。
r015|player|カザマル。俺の目の届かないところへ飛べ。何かあったら、誰より早く知らせろ。
r016|kazamaru|任せろ。帰ってきたとき、飯くらい残しとけよ。
r016n1|narrator|カザマルの羽が広がりかけた。烏天狗の長と目が合うと、わざともう少しだけ広げる。隣のユズハに肘をつつかれ、そこで止めた。
r017|player|スズネ。俺たちの行く道を開け。帰る場所も、頼む。
r018|suzune|ああ。椅子も道具も、直せるうちに直す。玉座の脚は、もうぐらつかないからな。
r018n1|narrator|茨木が自分の椅子を一度揺らし、スズネへ親指を立てた。スズネは頷き返し、慌てて正面へ顔を戻す。返事をする番を忘れかけていた。
r018n2|suzune|あ、ああ！　任せろ。……今の、もう一回言ったほうがいいか？
r019|player|ユズハ。隣にいてくれ。俺が見誤ったら、お前が正せ。
r020|yuzuha|では、遠慮はしません。あとで困った顔をしないでくださいね。
r020n1|narrator|ユズハの指が、袖の中で扇を探した。見つからない。今朝、顔を隠さずに返事をしようと、部屋へ置いてきたのだった。
r020n2|yuzuha|……よろしく、お願いします。これからも。
r021|narrator|あなたは四人の印へ触れた。里で初めて灯った紋が、今度は静かに輝く。あなたは広間へ向き直り、大きく息を吸った。
r021a|player|皆の者、面を上げよ。我こそは、十国を束ねる百鬼の――
r021b|yuzuha|長いです。練習では三行でした。
r022|player|この四人を、我が四天王とする。
r022n1|narrator|あなたの声は、広間の端まで届いた。四人の目には、まだ少しだけ信じられないという色があった。
r022n2|narrator|雪女が最初に手を叩いた。化け狸の頭領が慌てて続き、酒呑は杯を上げる。怨霊将軍は、眠っていなかった。まっすぐ立って、こちらを見ていた。
r023|narrator|広間に歓声が上がった。後ろに並べられていた四脚の椅子を、スズネが玉座の脇へ運ぶ。カザマルが反対側を持った。
r024|suzune|このくらい近くないと、話しづらいだろ。
r025|player|……そうだな。
r025n2|narrator|あなたが椅子を寄せると、五人が同じ段へ収まった。式の形を気にしていた者も、最後には手を叩いた。
@scene|village|紅葉の里／即位後の里帰り|akari,yuzuha
@bgm|daily
r026|narrator|式から数日後、五人は休みを合わせて里へ帰った。街道を歩き、途中の宿で一泊して着いた。今朝は、久しぶりに社で朝飯を食べる。
r026n1|suzune|門の金具、ちゃんと替えてあるな。あたしが書いていった寸法、読めたんだ。
r027|akari|王城へ、帰る日を伝えてある？　勝手にいなくなったことにしないでよ。
r028|player|明後日には里を発つと書いた。酒呑が持っている。今度は、ちゃんと守れる約束だ。
r028n1|narrator|アカリは鍋をかき回しながら、あなたを見た。言い返すことはしなかったが、頷くまでに、ひと呼吸あった。
r028n2|akari|じゃあ、今日はゆっくりして。薬を届ける家には、午後にみんなで行こう。
r029|yuzuha|帰ると書いたのに、戻ってこないと大騒ぎをされそうですね。あの方、案外さみしがりですから。
r030n1|narrator|社の床に荷物を置くと、旅の間についた泥が少し落ちた。誰かが使っていた部屋は暖かく、置いていった桶はもう漏れなくなっていた。
r030n2|akari|変わってるところもあるね。私たちがいない間、ちゃんと続いてたんだ。
r031|narrator|スズネが椀を五つ並べる。座る場所は、魔王の記憶を取り戻す前と同じだった。
r032|suzune|なあ。四天王って、屋根も直すのか。
r032n1|narrator|スズネは天井の染みを見上げた。修理した屋根の下に、一箇所だけ古い雨の跡が残っている。立とうとした膝へ、アカリが椀を置いた。
r032n2|akari|今は座る。雨、降ってないから。
r033|player|俺の城にも、直してほしいところがある。
r034|suzune|そっちは給金を出せよ。
r034n1|kazamaru|おれは屋根へ上がるだけで、飯一杯追加な。瓦まで運ばせるなら、もう一杯。
r034n2|yuzuha|そろそろ、王様より高くつく四天王になりそうですね。
r035|narrator|里の門で鐘が鳴った。早便の文だった。青葉の頭領と川霧の牛鬼が、境の橋を直す費用で揉めているという。祝いの席でも、まだ決まらなかったらしい。
r036|suzune|両方から直せばいいだろ。槌、持ってくる。
r037|yuzuha|工事代をどちらが払うか、という話でしょうね。王様に決めてほしいそうです。
r037n1|player|あいつら、祝いの席から同じ話をしてるのか。図面までつけてきたのか？
r037n2|narrator|二枚の図は、橋の幅からして違っていた。スズネが呆れた声を出し、その拍子に椀を傾ける。アカリが下から支えた。
r038|suzune|……飯を食ってからでいいな、これ。
r039|player|ああ。食べ終えたら返事を書く。明後日の帰りに橋で会おうと。二人とも呼んでおいてもらおう。
r039n1|akari|お使いの人にも、一杯持っていくね。空いてる椀、取ってくれる？
r039n2|narrator|あなたは棚へ手を伸ばした。背が足りず、カザマルに取ってもらう。王の記憶が全部戻っても、その棚は高いままだった。
r039n3|kazamaru|あと二、三年だな。そこに手が届くのは。
r039n4|player|覚えておけよ。届いたら、お前の分だけ上へしまう。
r040|narrator|カザマルが先に椀を抱え、笑い声が上がった。アカリが戻ってきたのを待ち、あなたも箸を取る。今朝の粥は、まだ湯気を立てていた。
`),
  ep('akari-01', 'akari', '第一話　その呼び方', '白嶺国・雪の御殿／本編第五話のあと', 'snow', ['akari', 'e050'], `
@bgm|warm
r001|narrator|白嶺の戦が終わった翌日。次の出発まで、あなたたちは雪の御殿に滞在していた。アカリは廊下で、配膳の盆を持ったまま立ち止まっている。
r002|e050|お食事は侍女が運びます。あなたはお休みください。
r003|akari|でも、いつも私が起こしてるから。
r004|e050|王はもうお目覚めです。朝の評議に出ておられます。
r004n1|akari|そうなんですか。じゃあ、終わるまでここで待っていても……。
r004n2|e050|評議が長引くかもしれません。お部屋へ置いていただければ、あとはこちらで。
r004n3|narrator|雪女が盆を受け取ろうとした。アカリは反射的に引いた。粥が縁まで跳ね、雪女の白い指へかかりそうになった。
r004n4|akari|ごめんなさい。……持っていたいんです。まだ。
r005|narrator|広間の戸が開いた。あなたは大勢の家臣に囲まれ、知らない土地の名を次々と口にしていた。
r006|akari|……ほんとに、起きてる。
r007|e050|昔も、戦の前にはよくお働きになりました。
r008|akari|そっか。私がいなくても、ちゃんとしてたんだ。
r008n1|narrator|里では、あなたを起こす声はいつも届いた。ここでは、広間の入口から席まで、見知らぬ人の肩がいくつも並んでいる。
r008n2|akari|聞いたことのない話ばっかり。あの人は、ちゃんとわかってるんですね。
r008n3|e050|わからないことばかりですよ。今の王も、私も。……昨日は、昔ならとばかり考えてしまいました。
r008n4|akari|……私も聞けばいいんだろうけど。何から聞けばいいのか、まだわからなくて。
r009|narrator|盆の上の粥は、二人分だった。アカリは自分の椀だけを持ち、台所へ戻る。
r010|player|アカリ。どこへ行く。
r011|akari|朝ごはん。……じゃなくて、お食事、です。
r012|player|何か悪いことをしたか、俺。
r013|akari|してない。みんなの前だし、ちゃんとしようと思っただけ。
r013n1|player|いつもの呼び方で誰かに何か言われたか。
r013n2|akari|違うよ。みんな、丁寧だから。私だけあの調子で入ったら、邪魔になると思って。
r013n3|player|邪魔ではない。今朝は、来ないのかと思っていた。
r013n4|akari|来てたよ。さっきから、ずっと。……もういい。ごはん、食べて。
r014|narrator|あなたは盆から椀を取った。座る場所を探す間に、家臣たちが慌てて道をあける。
r015|player|腹が減った。話は食べながら聞く。
r016|akari|お行儀、悪いよ。
r017|player|そっちは、いつもの声だな。
r018|narrator|アカリは少し笑った。けれど隣には座らず、盆を抱えたまま壁際へ下がった。
r018n1|narrator|あなたは空いた席を示したが、アカリはあとでと首を振った。評議の続きが始まり、二人の間にまた別の土地の名が入る。
r018n2|narrator|台所へ戻る途中、アカリは自分の粥を一口食べた。今朝は塩を入れるのを忘れていた。普段なら、あなたが先に気づく味だった。
r018n3|akari|次は、何食べたいって聞いてから作ろうかな。
r018n4|narrator|塩を入れ直す気にはなれなかった。アカリは薄い粥を飲み込み、盆を洗った。いつもの二枚の布巾を、少し離して干した。
`),
  ep('akari-02', 'akari', '第二話　三百年ぶんの手帳', '白嶺国・雪の御殿の書庫', 'snow', ['akari', 'e050'], `
@bgm|warm
r001|narrator|その夜、アカリは雪女の書庫を訪ねた。机には、あなたの昔の習慣を書いた手帳が何冊も並んでいた。
r002|akari|好きなもの、教えてもらってもいいですか。昔の。
r003|e050|なぜ、私に。
r004|akari|本人に聞くと、今は何でもいいって言うから。
r004n1|akari|私が好きだと思ってたもの、全部、子供の頃のだけだから。もう違うのに、言わずに食べてるのかもしれないって。
r004n2|e050|私が用意した昔の菓子も、半分残っていました。今の身体には、甘すぎたようです。
r004n3|narrator|雪女は手帳を自分の側へ揃え、アカリのために空いた椅子を引いた。読む前に、お茶を一杯ずつ置く。
r004n4|e050|それでも、召し上がろうとなさるのです。そういうところばかり、変わらない。
r005|narrator|雪女は一冊を開いた。料理、着物、苦手な薬。その間に、出陣した日と戻った日が細かく記されている。
r006|e050|蓮の実の菓子を好まれました。辛いものは、平気なふりをされます。
r007|akari|それ、今も。すぐお茶飲むんです。
r007n1|akari|飲むの、ばれてないつもりなんです。湯飲みだけ先に空になるのに。
r007n2|e050|昔は私が注ぎ足していました。辛くない物へ替えましょうかと聞くと、結構だとおっしゃって。
r007n3|akari|じゃあ、それは信じなくていいんですね。今度、横に辛くない皿も置こう。
r007n4|narrator|二人はその頁に印を挟んだ。アカリは、覚えておきたいと思った箇所だけを、自分の小さな紙へ写した。
r008|narrator|二人は笑った。アカリが次の頁をめくると、日付だけが続いていた。戻った日は、書かれていない。
r009|akari|……ごめんなさい。
r010|e050|書くことをやめると、帰ってこないと認めるようで。
r010n1|akari|帰った日、今日は書いていいんですよね。
r010n2|e050|ええ。けれど、どこへ続ければよいのか迷っていました。最後の頁から始めるには、間が長すぎて。
r010n3|narrator|アカリは白い頁を見た。毎日つけた日付の途中から、月だけになり、やがて年だけになっていた。
r010n4|akari|その頁、残しておいて。私、こんなに待ったこと、ないから。
r011|narrator|アカリは手帳を閉じた。借りて帰るつもりだったが、机の上へ戻す。
r012|e050|覚えなくてよいのですか。
r013|akari|これは、あなたが知ってるあの人だから。
r014|e050|では、あなたが知るあの方は？
r015|akari|朝に弱くて。大事なことほど一人で考えて。痛いときに、平気な顔する。
r016|e050|それは、昔からです。
r017|narrator|雪女は空白の頁を切り取らずにめくった。新しい頁の上へ、今日の日付を書いた。
r018|e050|これからは、ときどき教えてください。
r018n1|narrator|雪女は新しい日付の横へ、アカリに筆を渡した。アカリは迷った末、朝の粥の塩を忘れたことを書いた。
r018n2|e050|それは、王のお好みの記録ですか。
r018n3|akari|次に忘れないための、私の記録です。今度食べてもらったら、ちゃんと感想も書きます。
r018n4|narrator|雪女は筆を置いた。アカリの字は大きく、一行ぶんもらった場所を二行使っていた。二人とも、書き直そうとは言わなかった。
`),
  ep('akari-03', 'akari', '第三話　火を大きくするだけじゃ', '白嶺国・御殿の裏庭', 'snow', ['akari', 'suzune'], `
@bgm|daily
r001|narrator|出発まであと二日。アカリは裏庭で、火の結界を練習していた。大きく張ると雪が溶け、小さくすると誰かの足がはみ出す。
r002|suzune|これ、あたしの槌まで入るか？
r003|akari|入る。もうちょっと寄って。
r004|suzune|もう寄ってる。お前の肘、腹に当たってるぞ。
r004n1|suzune|全員くっついて歩くわけにはいかないぞ。あたしが槌を振ったら、隣のやつに当たる。
r004n2|akari|戦ってる間も守れるようにしたいんだけど、誰かが離れると、そこだけ薄くなるの。
r004n3|suzune|じゃあ、まずあたしが動く。どこまで離れたら薄いか、言ってくれ。
r004n4|narrator|スズネが横へ二歩動いた。アカリは追いかけずに火を伸ばしたが、反対側の明かりが消えかける。
r005|narrator|結界が揺らいだ。アカリが力を込めると、周りの雪が一気に蒸気になった。視界が真っ白になる。
r006|akari|あっつ！　待って、消す！
r007|suzune|守られてるほうが煮えるぞ、これ！
r008|narrator|二人が雪の上へ飛び出す。あなたは少し離れたところで、火が消えるのを待っていた。
@bgm|warm
r008n1|narrator|あなたは水の入った桶を持っていた。アカリは受け取り、火を置いた場所へ少しずつ撒く。溶けた雪の下には、細い草が残っていた。
r008n2|akari|私が立ってた所だけ、焼けてる。守ってるつもりで、誰も逃げられなくしたら……。
r008n3|narrator|白嶺の凍った門が、頭をよぎった。アカリは桶を抱え、黒くなった地面へ水をかけた。あなたは隣にしゃがんだが、手を出せなかった。
r008n4|akari|もう一回。今のままは嫌。強い火だけ出せても、嫌なの。
r009|player|もう少し力を送ろうか。
r010|akari|今はいい。強くしたいんじゃなくて、ちょうどよくしたいの。
r011|player|難しい注文だな。
r012|akari|味噌汁だって、火を強くすればいいわけじゃないでしょ。
r012n1|player|俺も中へ入る。危なくなったら逃げるから、遠慮するな。
r012n2|akari|逃げてね。本当に。私が消すの、待たないでよ。
r012n3|suzune|あたしは大きく動く。二人とも一緒の速さじゃ、練習にならないだろ。
r012n4|narrator|二人が別々の方向へ歩き始めた。アカリは大きな輪を諦め、いったん提灯へ火を戻した。
r013|narrator|アカリは四つの小さな灯を置いた。人の立つ場所だけを、別々に囲んでみる。今度は、間を冷たい風が通った。
r014|suzune|お。これなら動ける。
r015|akari|あんたも入って。勝手に外へ出ないでね。
r016|narrator|あなたの足元にも灯が一つ置かれた。五つの火が、雪の上でゆっくり揺れた。
r016n1|narrator|あなたが急に立ち止まると、一つの灯だけが足の先へはみ出した。アカリは位置を直し、今度は呼吸をひとつ遅くした。
r016n2|akari|止まるとき、肩が少し動くんだね。スズネは足より先に槌が動く。
r016n3|suzune|よく見てるな。でも、毎回同じ動きはしないぞ。
r016n4|akari|だから、もう一回。今日は少しだけ、間に合ったから。
`),
  ep('akari-04', 'akari', '第四話　こっちを見て', '白嶺国・南の峠／出発の日', 'snow', ['akari', 'e050'], `
@bgm|tense
r001|narrator|御殿を発つ朝、峠の雪が崩れた。斜面へずり落ちた荷車に、子供がしがみついている。あなたはスズネが構えた命綱を腰へ巻き、荷台へ近づいた。
r001n1|narrator|車輪は雪の縁へ半分乗り、御者は引き綱を放せずにいた。あなたが荷台を押せば、いったん道へ戻せるように見えた。
r001n2|player|後ろを持つ。カザマル、車輪に石を――。
r001n3|akari|先に下を見て！　雪の色がそこだけ違う！
r001n4|narrator|あなたは一度足を止めた。荷台の子が手を伸ばし、車輪がまた指一本ぶん滑った。
r001n4v1|narrator|背後でスズネが綱を張る音がした。あなたは荷台の子へ手を伸ばした。あと指一本ぶんで届く。それが、あと一歩に見えてしまった。
r002|akari|待って！　その下、空洞！
r003|narrator|踏み抜いた足が宙へ出た。腰に命綱が食い込み、スズネが雪を削って踏みとどまる。アカリの灯りが子供とあなたを囲み、吹きつける雪を顔の手前で弾いた。
r004|player|アカリ、俺はいい。荷車のほうを……。
r005|akari|よくない！　こっちを見て！
r006|narrator|あなたが振り向く。アカリの手は震えていた。強く燃やせば足場が溶ける。彼女は灯りを人の肌のすぐ外へ寄せ、雪女が縄の下の斜面を凍らせるのを待った。
r007|akari|綱、切れてない。スズネが持ってる。今から引くから、私の灯りがあるほうへ手を出して。
r008|player|……わかった。
r008n1|akari|そこ、もう少し右。スズネの声がするほう。下じゃなく、こっちを見て。
r008n2|player|手が届かない。あと少し、右か。
r008n3|suzune|こっちから行く。王様は止まってろ。動くのは、あたし一人でいい。
r008n4|narrator|カザマルが荷台の子供を抱き上げた。あなたは綱を握り、足をばたつかせるのをやめた。揺れが収まると、スズネの指がようやく手首へ届いた。
r009|narrator|スズネがあなたを道へ引き上げた。足場の雪は溶けていなかった。子供を抱いたカザマルが降りるまで、アカリは火を消せずにいた。
@bgm|warm
r010|e050|見事でした。雪を溶かさずに、あれだけの人数を。
r011|akari|裏庭、いっぱい溶かしちゃいましたけど。
r012|e050|春の手入れが早く済みました。
r012n1|narrator|雪女は橇を呼び、荷物を載せ替える場所を指した。子供は道の上へ戻ってからも、カザマルの袖を握っていた。
r012n2|akari|私の火、熱くなかった？　腕、赤くなってない？
r012n3|narrator|子供が首を振ると、アカリはようやく息を吐いた。提灯の持ち手が、汗で手のひらに貼りついていた。
r012n4|e050|残りは私たちが運びます。あなたも、座ってから手を確かめなさい。
r013|narrator|雪女が先へ歩く。アカリはあなたの包帯を確かめ、結び目をきつくし直した。
r014|player|痛い。
r015|akari|痛かったら言う。さっきも。
r016|player|……はい。
r016n1|player|……腰、少し痛い。綱の跡だと思う。
r016n2|narrator|アカリが包帯を止めた。痛いと言えと、自分で言ったばかりだった。怒るために開いた口を閉じて、もう一度結び直す。
r016n3|akari|座って。今は、痛いほうを先に見せて。
r016n4|narrator|あなたは黙って腰を下ろした。アカリの手はまだ冷たく、震えていた。温めようと触れると、今度は彼女が目を逸らした。
r017|narrator|それを聞いたスズネが笑った。アカリは怒ったまま、あなたの袖を離さずに峠を下りた。
`),
  ep('akari-05', 'akari', '第五話　四天王の仕事', '幽京国・王城／本編終章の即位後', 'capital', ['akari', 'e050'], `
@bgm|daily
r001|narrator|即位の数日後。四天王として初めての評議を終え、アカリはあなたの部屋へ向かった。雪女も同じ廊下を歩いていた。
r002|e050|灯の四天王。よい呼び名がつきましたね。
r003|akari|いつの間にか広まってて。ちょっと、落ち着かないです。
r004|e050|私も初めて役目を頂いた日は、鏡に向かって名乗る練習をしました。
r005|akari|……私も昨日、しました。
r005n1|akari|評議で、変なこと言ってなかったですか。兵の分の薪を減らすなって、つい、大きい声で。
r005n2|e050|私も、少し驚きました。皆が数を見ているところで、凍えた顔の話をなさるので。
r005n3|akari|この前の子、指先がなかなか動かなくて。あれを思い出したら、黙っていられなくなって。
r005n4|e050|……ええ。あの子に私の外套は、まだ大きすぎますね。
r006|narrator|戸の向こうから、紙をめくる音がした。夕飯の膳が、手つかずで置かれている。
r007|akari|また。ねえ、開けるよ！
r008|player|あと一枚だ。
r009|akari|その一枚、朝も言ってた。
r009n1|player|朝の一枚とは別だ。これは、峠の宿へ返す手紙で……。
r009n2|akari|別なのはわかるよ。最後の一枚がずっと新しくなるから、終わらないんでしょ。
r009n3|narrator|あなたは反論しかけ、机の端に積んだ未開封の文を見た。アカリはそれを退けず、食事の場所だけ空けた。
r009n4|akari|全部やめろって言ってない。食べる間だけ、こっちを見て。
r010|narrator|アカリは机から筆を取り上げた。雪女が盆を置き、冷めた椀を温かいものに替える。
r011|player|お前たち、打ち合わせをしたのか。
r012|e050|必要ありませんでした。
r013|akari|食べて。私たちも、まだなんだから。
r014|narrator|あなたはようやく筆を諦め、席を空けた。アカリが隣へ座る。今度は、誰の顔色も見なかった。
r014n1|narrator|アカリはあなたの椀へ手を伸ばしかけ、自分で取れる位置にあると気づいて戻した。あなたが箸を持つまで、隣で待つ。
r014n2|player|今日は、もう食べさせなくていいぞ。
r014n3|akari|そこまでしてないよ。……里では何回か、したけど。
r014n4|e050|今夜は、ご自身でお食事を済ませた、と書いておきましょうか。
r015|player|四天王に、食事の世話まで頼んだ覚えはないぞ。
r016|akari|それは幼なじみのほう。四天王の仕事は、食べたらやる。
r016n1|player|では四天王の仕事は何だ。今日、決めてきたことがあるんだろう。
r016n2|akari|峠の宿へ、火を置いてくる。子供たちが夜に外へ出ても、帰り道が見えるやつ。……私も、一晩泊まるつもり。
r016n3|player|そうか。帰ったら、ここで聞かせてくれ。隣の膳も用意しておく。
r016n4|narrator|アカリは返事の代わりに、小さく肩を寄せた。あなたが茶を取るために動くと、離れて、自分の椀を持った。もう席を立つ理由は探さなかった。
r017|narrator|提灯の火が、三人分の膳を照らした。雪女の手帳には、その晩も短い一行が増えた。
`),
  ep('kazamaru-01', 'kazamaru', '第一話　開けない手紙', '鳴神国への道・野営地／入国前', 'grave', ['kazamaru', 'yuzuha'], `
@bgm|warm
r001|narrator|天狗の山へ向かう前夜。カザマルは焚き火の前で、封の切られていない手紙を眺めていた。故郷から届いて、三日経つ。
r002|yuzuha|燃やすなら、風下でお願いします。灰が鍋に入ります。
r003|kazamaru|燃やすとは言ってねえだろ。
r004|yuzuha|読むとも言っていませんね。
r004n1|kazamaru|中身、だいたいわかるんだよ。字も昔のままだし。
r004n2|yuzuha|読んでいないなら、昔と違うことが一行くらいあるかもしれません。
r004n3|kazamaru|お前、そうやって人に箱を開けさせるの、うまいよな。
r004n4|narrator|カザマルは封の端をなぞった。破ろうとしてやめた跡が、三日分重なって白くなっていた。
r005|narrator|カザマルは封を切った。あなたが近づくと、文を膝の下へ隠す。
r006|player|悪い知らせか。
r007|kazamaru|よくあるやつ。山へ戻れ、魔王から離れろ、今なら許す。
r008|player|戻りたいか。
r009|kazamaru|戻りたかった時期はある。
r009n1|player|その頃、俺たちには言わなかったな。
r009n2|kazamaru|山へ帰りたいって言ったら、里が嫌なんだと思われるだろ。そうじゃなかったんだよ。
r009n3|player|飛べるようになったところを、見せたかったのか。
r009n4|kazamaru|それもある。戻れって、あっちから言われてみたかった。……今来られても遅いけどな。
r010|narrator|彼は焚き火へ小枝を放った。火の粉を目で追い、それから、今まで話さなかった山のことを口にした。
r011|kazamaru|おれ、山で一番飛ぶのが下手だったんだよ。風が強くなると、すぐ落ちる。
r012|player|今は俺たちで一番速い。
r013|kazamaru|今はな。
r013n1|narrator|カザマルは指先の印を隠すように、手の甲を膝へ向けた。風が弱い夜だった。火の粉は、すぐ近くへ落ちた。
r013n2|player|力を渡したことが、気になるか。
r013n3|kazamaru|助かってる。それは本当だ。でも、これがなかったらって聞かれると、すぐ答えられねえ。
r013n4|player|お前がそうして手を隠すのは、嫌だな。渡したことまで、間違いだった気がする。
r014|narrator|手紙には、最後に一行あった。『借りた翼で、長の前へ出るな』。カザマルはその部分だけを折り込んだ。
r015|player|明日、出発を遅らせよう。
r016|kazamaru|同情すんなよ。
r017|player|俺を乗せて飛べ。見てほしい谷がある。
r017n1|kazamaru|そんなに谷を見たいなら、一人で歩いて行けよ。おれの落ちた所が、珍しいか。
r017n2|narrator|あなたが黙ると、カザマルはすぐに顔を伏せた。火へ入れた枝を、焦げた指先でいじる。
r017n3|kazamaru|……悪い。今の、忘れろ。連れていくから。
r017n4|kazamaru|夜明けに出る。寝坊したら置いてくぞ。朝の風じゃないと、見せられねえ場所がある。
`),
  ep('kazamaru-02', 'kazamaru', '第二話　落ちた場所', '鳴神国・雲居地方・天狗の山の麓', 'mountain', ['kazamaru', 'akari'], `
@bgm|warm
r001|narrator|翌朝、カザマルはあなたを抱えて山の麓へ飛んだ。追ってきたアカリが、道から二人へ手を振る。
r002|kazamaru|見せたい谷って、ここか。おれが一番落ちた場所だ。
r003|player|だから頼んだ。
r003n1|kazamaru|地図に名前、載ってたか。山の連中は、ただの窪みって呼んでたぞ。
r003n2|player|昔、お前が話しただろう。里へ来たばかりの頃、帰り道で翼を傷めたって。
r003n3|kazamaru|よく覚えてるな。あのとき、笑わなかったの、お前くらいだったから。
r003n4|narrator|カザマルはあなたを平らな岩へ下ろした。立てる場所を選ぶ手つきに、初めて来た者の迷いはなかった。
r004|narrator|崖の岩には、小さな傷が何本も残っていた。子供のカザマルが、落ちながら爪でつかんだ跡だった。
r005|kazamaru|上の風が急に変わるんだよ。みんなは乗れるのに、おれだけ毎回、下へ行く。
r006|player|下からは、どうやって戻った。
r007|kazamaru|壁を登った。たまに、底から吹き返す風に乗れた。
r007n1|player|登っている間に、風が変わる場所を覚えたのか。
r007n2|kazamaru|覚えないと、また落ちるからな。あの草だけ、先に上へ向く。そこを待ってた。
r007n3|akari|上から見てる人には、草の向きまで見えないね。
r007n4|narrator|カザマルは岩の裏へアカリを案内した。風を避けられる場所も、待つあいだ足を休められる場所も、順番に出てきた。
r008|player|その風を見せてくれ。
@bgm|daily
r009|narrator|カザマルは首をかしげ、崖の下へ降りた。しばらくすると、あなたの横を一気に飛び抜ける。
r010|akari|うわ、速っ！
r011|kazamaru|これ？　谷から戻るだけだぞ。
r011n1|akari|戻るだけって言うけど、今、私の髪が反対へ飛んだよ。上の風と別なんだ。
r011n2|kazamaru|狭いところでぶつかるからだ。広く羽を出すと、逆に回される。
r011n3|player|団扇を開かなかったのも、そのためか。
r011n4|kazamaru|半分だけでいい。全部開くと、風を受けすぎるんだよ。……いつも怒られてたやり方だけど。
r012|player|上から来る風より、速かった。
r013|kazamaru|……そうか？
r014|narrator|カザマルはもう一度下りた。今度は途中で翼を開き、崖の上へ大きく回り込む。戻ってきたとき、少しだけ笑っていた。
r014n1|narrator|着地したカザマルは、すぐに崖を振り返った。一度できたことが偶然ではないか、確かめる目だった。
r014n2|player|もう一回、同じ場所から出られるか。俺は上から見ている。
r014n3|kazamaru|やる。次は、少し右に出る。敵が待ってる場所へ、そのまま顔を出したくねえし。
r014n4|akari|戻ったら水を飲んで。楽しくなったからって、休むのを忘れないでよ。
r015|akari|ねえ、今度私も乗せて。
r016|kazamaru|酔うぞ。
r017|akari|じゃあ、今は見とく。
`),
  ep('kazamaru-03', 'kazamaru', '第三話　一人で飛べる', '鳴神国・雲居地方・谷の練習場', 'mountain', ['kazamaru', 'suzune'], `
@bgm|warm
r001|narrator|谷での練習は夕方まで続いた。カザマルは最後に、あなたから流れる妖力を止めてほしいと頼んだ。
r002|player|今の速さは出なくなるぞ。
r003|kazamaru|それを知っときたい。
r003n1|player|危なくなったら、すぐ戻す。それでいいな。
r003n2|kazamaru|ああ。でも、最初に押し戻されたところでは待ってくれ。そこから、どう動けるか知りたい。
r003n3|suzune|合図を決めよう。声が聞こえないと、こっちも待っていいかわからない。
r003n4|kazamaru|団扇を閉じて二回振ったら、助けてくれ。それ以外は、下の足場まで見ててほしい。
r004|narrator|印の光が消える。カザマルの肩が少し下がった。彼は羽を二度動かし、崖の縁へ立つ。
r005|suzune|落ちたら拾うから、変な方向へ行くなよ。
r006|kazamaru|槌で拾う気か？
r007|suzune|縄もある。
r007n1|narrator|スズネは縄を岩へ結び、あなたにも結び目を確かめさせた。カザマルはそれを見ると、少し肩の力を抜いた。
r007n2|kazamaru|声かけんなよ。お前の顔が見えると、頼っちまう。
r007n3|suzune|じゃあ見ない。背中向けてる。……嘘だ、半分は見てる。縄を持ってるからな。
r007n4|kazamaru|張らねえ。そういうのは、今日はもういい。
r008|narrator|一度目は風に押し戻された。二度目は岩に足を擦った。三度目、カザマルは翼を開くのをぎりぎりまで待った。
r009|narrator|風が身体を持ち上げる。頂上へは届かなかったが、崖の中腹に降り立てた。
r010|kazamaru|……できるじゃねえか。
r010n1|narrator|崖の中腹で、カザマルは足場へ手をついた。上から飛んだときより息は荒い。だが、助けの合図は出さなかった。
r010n2|suzune|そこ、立てるか？　次の岩まで無理するなよ！
r010n3|kazamaru|立てる！　昔はここで、日が暮れるまで休んでたんだ！
r010n4|narrator|彼は岩へ背を預け、一度だけ声を出して笑った。昔と同じ場所が、今日は戻る途中の休憩所になっていた。
@bgm|resolve
r011|player|前からできていた。俺は、その風の見つけ方を知らん。
r012|kazamaru|でも、長にはこれじゃ勝てねえ。
r013|player|だから俺の力も使え。お前が見つけた飛び方で。
r014|narrator|カザマルはしばらく、足元を見ていた。やがて崖の下から、あなたへ手を上げる。
r015|kazamaru|頼む。今度は遠慮なく使う。
r015n1|player|どのくらい流せばいい。朝と同じで足りるか。
r015n2|kazamaru|最初は半分。上へ出るところで増やしてくれ。どこで足りなくなるか、今ならわかる。
r015n3|narrator|あなたは頷き、印へ触れた。カザマルは光の強さを見ず、谷の草が上を向く瞬間を待った。
r015n4|suzune|帰ってきたら、その飛び方を図にしてくれ。下で動く側も、覚えときたい。
r016|narrator|印に光が戻った。次の一飛びで、彼は崖の上を越え、夕日に溶けるほど高く舞い上がった。
`),
  ep('kazamaru-04', 'kazamaru', '第四話　返事は面と向かって', '鳴神国・雲居地方・天狗の山の野営地／本編第八話の前夜', 'mountain', ['kazamaru', 'yuzuha'], `
@bgm|warm
r001|narrator|長に挑む前の晩。カザマルは、返事を書こうとして筆を止めていた。紙には『明日』とだけある。
r002|yuzuha|短いですね。
r003|kazamaru|明日行くんだから、明日でいいだろ。
r004|yuzuha|それなら紙もいりません。顔を見せれば済みます。
r004n1|kazamaru|返事くらい、立派に書こうと思ったんだよ。何言われても困らねえように。
r004n2|yuzuha|全部書けば、相手は一番困るところだけ選んで読みますよ。
r004n3|kazamaru|……だったら、本当に顔を見せたほうが早いな。
r004n4|narrator|カザマルは紙を裏返した。大きく書いた『明日』の裏には、まだ何もない。そこへ、谷の形を書き始めた。
r005|narrator|カザマルは笑い、筆を置いた。代わりに地図を広げる。谷の風と、長が逃げそうな岩場に印がついていた。
r006|kazamaru|ここ、頼めるか。長が降りたら、二人で止めてほしい。
r007|yuzuha|一対一で、勝ちたかったのでは？
r008|kazamaru|最初はな。でも、勝って道を開けるのが先だ。
r008n1|yuzuha|一対一で勝った顔は、見たいですね。あなたが三日くらい、ずっと自慢するところも。
r008n2|kazamaru|三日で済むかよ。……ああ、くそ。やっぱり殴りたくなってきた。
r008n3|yuzuha|では、私たちが道を空けたら、一発だけ。その間に門の鎖を切ります。
r008n4|narrator|カザマルは地図へ、長の立つ場所と、荷を逃がす場所を別々に記した。
r009|narrator|あなたが水を置くと、カザマルは地図を隠さず指で叩いた。
r010|kazamaru|おれが上に出たら、合図してくれ。下からだと、兵の動きが見えない。
r011|player|わかった。
r011n1|kazamaru|あと、おれが上へ出たら、追いかける前に一回呼んでくれ。あいつの顔を見てると、たぶん周りを忘れる。
r011n2|player|わかった。呼ばれても聞かないときは、アカリにも頼む。
r011n3|kazamaru|アカリまで呼ぶなよ。……いや、呼べ。腹立つけど、たぶんそれで止まる。
r011n4|narrator|あなたは地図の端へ合図を書いた。カザマルはそれを消さず、明日誰が持つかをユズハへ相談した。
@bgm|resolve
r012|kazamaru|長に何を言われても、手は出すなよ。
r013|player|お前が無茶をしなければな。
r014|kazamaru|それ、お互い様。
r014n1|player|お前に言われると、言い返しにくいな。
r014n2|kazamaru|なら、二人とも覚えとけるだろ。片方が忘れたら、もう片方が止める。
r014n3|yuzuha|二人同時に忘れた場合も考えて、私たちも見ています。地図を貸してください。
r014n4|narrator|三人で岩場の印を見直した。風向きの違う場合の道を書き足すと、筆を置く頃には、明日やることが手紙よりずっとはっきりしていた。
r015|narrator|寝る前、カザマルは故郷の手紙を畳み、袋へ戻した。焚き火には入れなかった。返事は、明日直接伝えることにした。
`),
  ep('kazamaru-05', 'kazamaru', '第五話　風の四天王、配達中', '紅葉の里・社の屋根／本編終章のあと', 'village', ['kazamaru', 'akari'], `
@bgm|daily
r001|narrator|即位後、里へ戻ったカザマルの袋には、城の文書と団子の包みが一緒に入っていた。里の子供が、四天王様だ、と追いかけてくる。
r002|kazamaru|様はいらねえ。包みを踏むな、団子だぞ。
r003|akari|お届け物？
r004|kazamaru|城の印がいる書類。ついでに、お前らが帰らない理由を見てこいって。
r004n1|kazamaru|帰る日が変わってないか聞いてこいって。文なら運ぶけど、五人分の荷は運べねえからな。
r004n2|player|変わっていない。この便で返事を出す。団子は、ついでか。
r004n3|kazamaru|こっちが先。書類はそのあとに渡された。潰さないように、ずっと上へ置いてきたんだぞ。
r004n4|narrator|アカリは包みを受け取り、帳面と分けて棚へ置いた。その前にカザマルが二本抜き、油紙へ包んで胸元へしまった。
r005|player|屋根を直していた。
r006|kazamaru|また落ちたのか。
r007|player|今日は落ちてない。
r007n1|suzune|作業は済んだ。乗るなら真ん中だけな。端の瓦、まだ留めてない。
r007n2|kazamaru|聞いたか。四天王の注意はちゃんと聞けよ、王様。
r007n3|player|お前は、四天王になる前からそう言っていた。
r007n4|narrator|カザマルは笑い、あなたが屋根で座れる場所を上から確かめた。今日は、急いで高く上がる必要はなかった。
r008|narrator|カザマルはあなたを屋根まで運んだ。降ろしてから、少し離れた棟へ座る。昔は二人で、よくここから山を眺めていた。
r009|kazamaru|あの山より先に行くって言ってたの、覚えてるか。
r010|player|お前が落ちなくなったら、連れていってくれると言った。
r010n1|kazamaru|あのときは、山を越えれば何でもできる気がしてた。でも、越えた先にも山があったな。
r010n2|player|川もあったし、道を塞ぐ奴もいた。そのたび、お前が先を見てきてくれた。
r010n3|kazamaru|見て帰る場所があったからだよ。一人でどこまでも行けって言われたら、たぶん飽きる。
r010n4|narrator|遠くの山へ、荷を運ぶ天狗の列が向かっていた。カザマルは列の間隔を目で追い、先頭が風を避けたのを見て頷いた。
r011|kazamaru|連れてったな。国の端まで。
r012|player|ああ。次も頼む。
r013|narrator|カザマルは頷き、胸元の油紙から団子を一本投げた。あなたが取り損ねるより早く、風が手元へ押し戻す。
r014|kazamaru|それ、四天王の給金で買ったやつだから。落とすなよ。
r014n1|player|山の連中にも買っていったのか。
r014n2|kazamaru|ああ。戻るたび、何か教えろって寄ってくるからな。食ってる間くらいは静かになる。
r014n3|player|お前も、教える側になったんだな。
r014n4|kazamaru|落ちた話ばっかりだぞ。でも、それを聞きたがるやつもいるんだよ。
r015|akari|上の二人！　食べるなら降りてきて！
r016|narrator|今度は自分の足で梯子を下りた。カザマルは先に地面へ降り、あなたが下りきるまで、その下で待っていた。
`),
  ep('suzune-01', 'suzune', '第一話　直すものが多すぎる', '青葉城・工房／本編第六話より前', 'meadow', ['suzune', 'akari'], `
@bgm|daily
r001|narrator|次の遠征を控えた日。青葉城の工房には、壊れた門の金具や兵の槍が積まれていた。スズネは自分の槌だけを、机の下へ隠している。
r002|akari|その槌、ひび入ってない？
r003|suzune|あとで直す。先にこっちだ。明日の見張りが使う。
r003n1|akari|昨日も、先に見張りの槍って言ってた。その前は荷車。自分のは、いつ直すの？
r003n2|suzune|順番はつけてる。今すぐ困るやつからやってるだけだ。
r003n3|narrator|アカリは机の端にあった紙を見た。修理待ちの品が隙間なく書かれ、スズネの槌の名前だけが、最後尾で消しては書き直されていた。
r003n4|akari|これ、終わるたびに新しい物が先へ入ってる。順番、回ってこないよ。
@bgm|warm
r004|narrator|柄を握るたび、手のひらに血が滲んだ。アカリが金具を取り上げると、スズネは困ったように笑った。
r005|suzune|力が増えたら、仕事も早くなると思ったんだけどな。
r006|akari|仕事の量も増えてるから。
r006n1|suzune|一回で打てると思って力を入れると、金具のほうが潰れる。結局、前より気を遣ってるんだ。
r006n2|akari|手のほうも潰れかけてる。よく見て、ここ。さっきから握り直してるでしょ。
r006n3|suzune|……見てたのか。じゃあ、隠しても無駄だな。
r006n4|narrator|スズネが手を開くと、小さな木片が皮膚へ刺さっていた。アカリは火を近づけすぎない位置に置き、一本ずつ抜き始めた。
r007|player|工房に人を増やす。必要な道具を書いてくれ。
r008|suzune|でも、里の癖を知ってるの、あたしだし。
r009|player|教えればいい。
r010|suzune|教えてる間に、自分でやったほうが早い。
r010n1|player|次の遠征のとき、俺はお前が来ないほうが怖い。
r010n2|suzune|戻ってきて、門が閉まらなかったら嫌だろ。あたしがやったやつなら、どこが壊れたかわかる。
r010n3|player|門の金具は、俺も手伝う。ここの形くらいなら覚えた。お前は横で文句を言っていろ。
r010n4|narrator|あなたが鑢を逆に持つと、スズネは即座に取り上げた。持ち直させてから、修理待ちの紙へ図を描く。思っていたより、何度も描き直すことになった。
r011|narrator|あなたは机の下から槌を出した。頭の亀裂は、思っていたより深い。
r012|player|これは誰が直す。
r013|narrator|スズネは答えなかった。アカリが椅子を引き、そのまま彼女を座らせる。
r014|akari|今日は手から。包帯、巻くよ。
r015|suzune|……きつくすんなよ。明日、握れなくなる。
r016|akari|明日は休むの。
r016n1|suzune|休んでいる間に、誰かが困ったら呼べよ。勝手に平気だってことにするな。
r016n2|akari|呼ぶ。でも、困ってないのに見に来るのもだめ。今は私が手を直す番だから。
r016n3|player|槌は紅峰へ持っていこう。あそこの炉なら、頭の亀裂まで直せると聞いた。
r016n4|narrator|スズネは少し迷い、槌を机の上へ置いた。修理待ちの最後尾から名前を消し、持っていく荷物の欄へ書き直した。
`),
  ep('suzune-02', 'suzune', '第二話　お前の手に合わせろ', '焔谷国・鍛冶砦／本編第六話のあと', 'volcano', ['suzune', 'e060'], `
@bgm|warm
r001|narrator|焔谷城の戦のあと。輪入道は、スズネに炉の前へ来るよう命じた。槌を直すには、使い手の腕を見なければならないという。
r002|e060|振ってみろ。力は抜け。
r003|suzune|抜いたら、飛ばないぞ。
r004|e060|飛ばせとは言っておらん。ここは工房だ。
r004n1|narrator|輪入道は作業台を空け、床へ二本の線を引いた。スズネの足がどこから動くかを、先に見ておくという。
r004n2|e060|肩へ力を入れるな。いつも釘を打つときの速さでいい。届かせようとして、腕だけ伸ばすなよ。
r004n3|suzune|敵の前で、そんなゆっくり振ったことないぞ。
r004n4|e060|今日は敵の前ではない。修理を頼んだ職人の前だ。間違えるな。
r005|narrator|スズネは小さく槌を振った。輪入道が肘を押し、握る位置を指二本ぶんずらす。
r006|e060|柄を長くしたのは、誰の真似だ。
r007|suzune|昔見た、鬼の大将。あたしより、ずっとでかかった。
r008|e060|そいつの手に合わせたままじゃ、そいつより強くはなれん。
r008n1|suzune|あの大将は、一振りで門を開けたんだ。長い槌なら、同じことができると思って作った。
r008n2|e060|お前の肩幅も、腕の長さも違う。相手の道具の寸法だけ真似て、身体を後から合わせる気か。
r008n3|narrator|輪入道は柄に古い擦れ跡を見つけた。本来握る位置より内側へ、何度も手を戻してきた跡だった。
r008n4|e060|手のほうが先に気づいていたようだな。あとは、お前がそこへ合わせればいい。
r009|narrator|輪入道が柄の端に印をつける。スズネはしばらく迷い、それから自分で鋸を取った。
r010|suzune|……ここまで、切る。
r011|e060|思い切ったな。怖くはないのか。
r012|suzune|怖い。これで、ずっとやってきたから。
r012n1|suzune|短くして届かなくなったら、あの門の前で困るかもしれない。
r012n2|e060|一歩、近づけ。その一歩を踏む前に止まれる道具にする。戻せない振りを繰り返すよりは、長く使える。
r012n3|narrator|スズネは線の上へ立ち、まだ切っていない柄を内側で握った。一振りして戻すと、踵が床から離れなかった。
r012n4|suzune|……戻れるな。さっきより、ちゃんと自分で止められる。
r013|narrator|切った柄が床へ落ちる。握り直すと、槌の頭がいつもより近かった。肩に、無理な重さがかからない。
r014|e060|よし。ようやく、お前の道具になった。
r015|narrator|スズネは新しい握りに、自分の印を刻んだ。里の大工として使っていた、少し曲がった三本線だった。
r015n1|narrator|輪入道は切り落とした柄を捨てずに、作業台の端へ置いた。頭の修理が冷めるまで、握りを削る道具として使えと言う。
r015n2|suzune|古いところも、残るんだな。全部替わるのかと思ってた。
r015n3|e060|使える部分まで捨てるな。ここで覚えて帰れ。次の手入れは、お前がやるんだからな。
r015n4|narrator|スズネは鋸屑を払い、隣の作業台へ椅子を持ってきた。教わるために座るのは、ずいぶん久しぶりだった。
`),
  ep('suzune-03', 'suzune', '第三話　任せるのは難しい', '青葉城・工房／鍛冶砦からの帰還後', 'meadow', ['suzune', 'yuzuha'], `
@bgm|daily
r001|narrator|新しい槌を持って青葉へ戻ると、工房には弟子を志願する小鬼たちが来ていた。スズネは、まず門の留め具を任せた。
r002|suzune|違う、そこは裏だ。あー、貸せ。あたしが……。
r003|yuzuha|スズネ。
r004|narrator|伸ばした手が止まる。小鬼は留め具を抱えたまま、縮こまっていた。
r004n1|narrator|机には、スズネが出発前に描いた図があった。使い慣れた自分にはわかるが、初めて見る者には、表と裏の説明がなかった。
r004n2|yuzuha|図を見せてくれました。書いたとおりに作ろうとは、していたようです。
r004n3|suzune|書いた本人がここにいないとわからない図じゃ、だめだな。
r004n4|narrator|スズネは小鬼の手から部品を取らず、隣へしゃがんだ。同じ向きから金具を見ると、迷った理由が見えてきた。
r005|suzune|……なんで、裏だと思った？
r006|narrator|小鬼は部品をひっくり返して見せた。古い印が逆についている。前の城主の頃から、その向きで使われていたらしい。
r007|suzune|……これ、前から逆だったのか。あたし、見もしなかったな。
r008|yuzuha|あなたが出かけたあと、三人で何度も裏返していましたよ。聞く人がいなくて。
r009|suzune|わかった。わかったから、先にそれ言ってくれよ……。
r009n1|narrator|スズネは図を引き寄せ、自分で描いた線を塗り潰した。紙が破れた。小鬼がびくりと肩を跳ねさせ、スズネは手を止めた。
r009n2|suzune|お前のせいじゃない。……あたしが、急いで描いたからだ。もう一枚くれ。
r009n3|narrator|小鬼は指で図を確かめ、留め具を置き直した。スズネは間違えそうなところで口を開きかけたが、最後まで待った。
r009n4|suzune|そう。そこまで合ってる。最後の釘だけ、一緒に打とう。
@bgm|warm
r010|narrator|夕方、門は無事に閉まった。スズネが触る前に、小鬼たちが自分で確かめている。彼女は腕を組み、後ろで待った。
r010n1|narrator|一度閉め、もう一度開ける。小鬼たちは交代で金具を引き、力の弱い者でも動かせるかを試した。
r010n2|suzune|雨が降ったときも見てくれ。木が膨らむと、こっちへ寄るから。
r010n3|narrator|小鬼が閉じた門を、スズネはすぐ引き直しかけた。空を掴んだ手を腰へ戻す。弟子は気づかず、自分で留め具を二度引いた。
r010n4|narrator|ユズハが横へ来て、何も言わずに茶を渡した。スズネは両手で受け取った。手が塞がると、少しだけ待ちやすかった。
r011|player|どうだ。
r012|suzune|ちょっと遅い。穴も一個、曲がってる。
r013|player|やり直すか。
r014|suzune|閉まる。明日も閉まるなら、いい。
r014n1|suzune|……いや、明日の朝もあたしが見る。見るだけ。叩き直しはしない。
r014n2|player|茶を持って来ようか。
r014n3|suzune|いらねえ。今、口に出して我慢してるんだから、笑うな。
r014n4|narrator|あなたが先に歩き出すと、スズネはもう一度だけ振り返った。小鬼たちが門を閉め、勝手に拍手をしていた。声をかけるのはやめにした。
r015|narrator|工房へ戻る足取りは、朝より軽かった。初めて、自分の槌を手入れする時間が残っていた。
`),
  ep('suzune-04', 'suzune', '第四話　勝ったあとに立つもの', '荒磯国・鬼岳地方・鬼岳砦／本編第九話のあと', 'oni', ['suzune', 'e090'], `
@bgm|warm
r001|narrator|茨木との戦いが終わり、次の遠征を控えた夜。スズネは崩れた広場を直していた。助けた若い鬼たちが、板を運んでいる。
r002|e090|明日は出陣だろう。休まないのかい。
r003|suzune|帰ってきたやつが、同じ穴に落ちたら困る。
r003n1|narrator|板は穴より長く切ってあったが、置けば済むわけではなかった。スズネは下へ灯りを落とし、支えを入れる場所を探している。
r003n2|e090|上から塞ぐだけでは、また抜けるか。
r003n3|suzune|前にそれをやったから抜けたんだろ。穴を見えなくするだけなら早い。でも、帰ってくる兵は上を歩くんだぞ。
r003n4|narrator|茨木は広場の端を見た。昼のうちに、自分の兵が運んだ板と杭が揃えてあった。
r004|e090|俺の兵まで働かせてるのかい。
r005|suzune|手伝うって言うから。暇そうに見てるやつより助かる。
r006|narrator|茨木は笑い、板を一枚持ち上げた。スズネに置き場所を指され、言われたとおりに下ろす。
r006n1|suzune|そこ、少し上げて。下に当て木を入れる。力があると、こういうとき助かるな。
r006n2|e090|殴り合った相手に、板の重しをやらされるとは思わなかったよ。
r006n3|suzune|嫌なら替わる。でも、あんたが持つと両手が空くから、早いんだ。
r006n4|narrator|茨木は持ち替えずに待った。釘を打つ音が止まるたび、置いてよいかを目で尋ねる。
r007|e090|お前、鬼の軍を任されたらどうする。
r008|suzune|飯の数を調べる。寝る場所も。
r009|e090|勝ち方は？
r010|suzune|そのあとだ。腹減ってたら、あたしだって槌を振れない。
r010n1|e090|俺も飯の数は見てたさ。けど、床の下までは見てなかったね。
r010n2|suzune|あたしも、人のこと言えねえけど。壊れるってわかってるやつほど、あとに回すんだよな。
r010n3|narrator|若い鬼が箒を立てた。雨のあと、どこから泥が出たかを、棒で石へ描いて見せる。
r010n4|narrator|茨木は図の泥の溜まる場所を指した。若い鬼が頷くと、今度は二人で広場の端へ向かった。スズネは追わず、残った釘を拾い始めた。
r011|narrator|茨木は小鬼たちを見た。板を運ぶ者、釘を数える者。さっきまでばらばらだった兵が、スズネの一声で動いていた。
r012|e090|あの魔王、面白い将を拾ったね。
r013|suzune|逆。里で何もできなかったのを、あたしらが拾ってた。
r014|e090|じゃあ、最後まで拾ってやりな。あいつはまた、前へ出たがるよ。
r014n1|suzune|一人じゃ重い。あいつ、意地張ると余計に重くなる。
r014n2|e090|俺も、その中に入ったわけかい。
r014n3|suzune|ここまで板を持ったんだ。もう知らないふりはさせねえぞ。
r014n4|narrator|茨木は頷いて、打ち終わった釘を足で避けた。戦いの間は見ていなかった足元を、今夜は二人とも確かめながら歩いた。
r015|narrator|スズネは最後の釘を打った。板の上へ乗り、二度跳ぶ。下の穴は、もう見えなかった。
`),
  ep('suzune-05', 'suzune', '第五話　四脚の椅子', '幽京国・王城の工房／即位の前夜', 'capital', ['suzune', 'kazamaru'], `
@bgm|daily
r001|narrator|即位の前夜。スズネは四脚の椅子を工房へ運び込んでいた。四天王の席だという。どれも立派だが、同じ形だった。
r002|kazamaru|これ、おれの羽が当たる。
r003|suzune|だろうな。背を少し削る。
r003n1|kazamaru|立派な椅子だから、そのまま座るしかないのかと思ってた。
r003n2|suzune|座れないなら直す。王城の物でも同じだ。飾っとくために四つ並べるんじゃないだろ。
r003n3|narrator|スズネはカザマルを座らせ、羽が動く幅を測った。背もたれを低くしすぎないよう、細い線を何本も引く。
r003n4|kazamaru|ここから先なら、削っていい。あ、そこは残して。疲れると寄りかかるから。
r004|narrator|ユズハの席には尾を通す隙間。アカリの席には提灯を掛ける金具。スズネは自分の椅子だけ、脚を太くした。
r005|kazamaru|自分のは、ずいぶん頑丈にするんだな。
r006|suzune|寝落ちしても倒れないようにな。
r006n1|suzune|あたしのだけ壊れたら、直した本人が間抜けだろ。それに、槌を横へ立てる場所も欲しい。
r006n2|kazamaru|式の最中も持ち込むのかよ。
r006n3|suzune|式が済んだあとも使う椅子だぞ。毎回、武器を置きに別の部屋へ行くのは面倒だ。
r006n4|narrator|カザマルはそれを聞いて、自分の席にも地図を置ける小さな棚を頼んだ。注文は、座ってみるほど増えていった。
r007|player|俺の席はどうだ。
r008|narrator|あなたが覗くと、スズネは玉座の脚を叩いて聞かせた。一本だけ、鈍い音がする。
r009|suzune|ここ、直す。明日、ひっくり返ったら困るだろ。
r009n1|player|見えるところは立派なのに、底は傷んでいたんだな。
r009n2|suzune|ここだけ床に触れてない。飾りで隠れてたから、動かすまでわからなかった。
r009n3|player|古いからと、そのまま使わせるところだった。お前が見てくれてよかった。
r009n4|narrator|スズネは新しい木を当て、音を聞き直した。見えない側へ修理した日を小さく刻み、あなたにも場所を教えた。
r010|player|助かる。……椅子を置く場所は、後ろになると聞いたが。
r011|suzune|嫌か？
r012|player|顔が見えん。
r013|narrator|スズネは床に玉座の位置を描いた。その左右へ、椅子を二脚ずつ並べる。五人が、同じものを見る向きになった。
r014|suzune|じゃ、こうしよう。明日、あたしが動かす。
r015|kazamaru|運ぶの手伝う。そこでだけは飛ぶなって言うなよ。
r015n1|suzune|飛ぶと木屑が舞う。運ぶときは、歩いてくれ。ちゃんと道を空けてあるから。
r015n2|kazamaru|ああ、わかったよ。こういうときは、お前の言うことを聞く。
r015n3|narrator|あなたは完成した椅子を一脚ずつ確かめた。形は揃っていないが、誰の席かは、名札を置く前からわかった。
r015n4|player|明日から、ここで話すんだな。……今夜、一度座ってみるか。まだ式の言葉を考え直したい。
r016|narrator|スズネが小さな槌で仕上げの釘を打つ。国を取った大槌は、工房の壁に立てかけられていた。
`),
  ep('yuzuha-01', 'yuzuha', '第一話　まだ話していないこと', '風越国・風越地方・城の客間／本編第三話のあと', 'bamboo', ['yuzuha', 'akari'], `
@bgm|warm
r001|narrator|風越城を落とした夜。ユズハは、昔使っていた部屋で小さな箱を探していた。里から九尾へ送った報告書の控えが、そこに残っているはずだった。
r002|akari|手伝おうか。
r003|yuzuha|……見ても、楽しいものではありませんよ。
r004|akari|探し物なら、二人のほうが早いでしょ。
r004n1|narrator|ユズハは机の引き出しを開けた。空になった奥に、紙の端だけが挟まっている。昔の自分は、他人の探し方まで考えて隠していた。
r004n2|yuzuha|底が外れます。そこへ指を入れてください。角に棘があるので、気をつけて。
r004n3|akari|こんなところまで覚えてるんだね。
r004n4|yuzuha|忘れたつもりでした。来たら、手のほうが先に動きました。
r005|narrator|アカリは一枚を拾った。そこには、社の場所、里の人数、そしてあなたの名前が書かれていた。
r006|akari|この頃から、見てたんだ。
r007|yuzuha|ごめんなさい。
r008|akari|これ、本人は知ってる？
r009|yuzuha|仕事で来たことは。何を書いたかまでは、まだ。
r009n1|akari|書いた人は、あんた一人？　私たちが話したこと、ほかの人も聞いてた？
r009n2|yuzuha|私一人です。九尾様へ送ったあとは……誰が読んだか、まだ。
r009n3|akari|そっか。私、知らないまま、よその家のことまで話してたかもしれない。
r009n4|narrator|ユズハは紙の角を揃え始めた。アカリがその手を押さえる。きれいに並べたからといって、答えが出るわけではなかった。
r010|narrator|アカリは紙を折らず、ユズハへ返した。箱の蓋も閉めない。
r011|akari|私に先に謝らないで。あの人に、自分で渡して。
r012|yuzuha|怒るでしょうか。
r013|akari|わかんない。怒っても、聞いてくれるとは思う。
r013n1|yuzuha|里へ戻る資格は、もうないのでしょうね。
r013n2|akari|それ、私に言わせたいの？　帰らないでって。……ずるいよ、今そういう言い方するの。
r013n3|narrator|ユズハは口を閉じた。アカリも言い過ぎた顔をしたが、取り消さなかった。二人の間へ、開いたままの箱が残った。
r013n4|akari|この一枚、何が書いてあるか教えて。続きは、そのあとにする。
r014|narrator|ユズハは報告書を揃えた。一番下に、封を切っていない古い命令書が残っていた。
r015|yuzuha|……これも、持っていきます。
r015n1|narrator|封を切っていないのは、届いた命令の写しだった。ユズハは原本を読んだ日のことを、まだ覚えていた。
r015n2|yuzuha|あの人が熱を出した冬の命令です。原本は里で燃やしました。ここに写しが残っているとは、思わなくて。
r015n3|akari|じゃあ、今度は燃やさずに持っていこう。言葉だけで済ませないほうがいい。
r015n4|narrator|ユズハは新しい紐で箱を結んだ。隠し場所へ戻さず、明日持って出る荷物の一番上へ置いた。
`),
  ep('yuzuha-02', 'yuzuha', '第二話　里へ来た日', '紅葉の里・社の縁側／数年前の回想', 'village', ['yuzuha', 'suzune'], `
@bgm|mystery
r001|narrator|ユズハが里へ来た日は、雨だった。九尾の命令は、社の地下を見張ること。怪しまれぬよう、同じ年頃の者と親しくすること。
r002|narrator|橋のたもとで立っていると、あなたが傘を持ってきた。傘の骨は一本、折れていた。
r003|player|社まで来るか。雨漏りはするけど、ここよりは濡れない。
r004|yuzuha|知らない狐を、入れていいんですか。
r005|player|だめなら、あとで一緒に怒られる。
r005n1|yuzuha|里の方に、先に聞かなくてよいのですか。私がどこから来たのかも知らないのに。
r005n2|player|雨がやんだら聞く。今ここで聞いてたら、傘がもたない。骨、見えてるだろ。
r005n3|narrator|あなたは折れた側を自分へ向けて歩いた。ユズハはその手つきを覚えた。仕事なら、その親切を利用すればよかった。
r005n4|yuzuha|……少しだけ、お邪魔します。道が乾くまで。
@bgm|daily
r006|narrator|社には三人いた。スズネは床板を外し、カザマルは濡れた羽を広げ、アカリは空いている器へ粥をよそっている。
r007|suzune|客か。そこの床、踏むなよ。まだ釘打ってない。
r008|yuzuha|……ここに、みんなで住んでいるんですか。
r009|akari|住んでるのはこの人。私たちは雨宿り。よかったら食べて。
r009v1|narrator|ユズハは、濡らさないように抱えていた包みを差し出した。里の若者が好む味は、もう調べてある。雨の中、買いに寄った団子だった。
r009v2|yuzuha|皆さんで、どうぞ。お好きだと聞いたので。
r009v3|akari|わ、よく知ってるね。じゃあ半分は食べたあと！　カザマル、まだ取らない！
r009v4|narrator|計画どおりに笑顔が返ってきた。粥の湯気を見た途端、聞いておくはずだった見返りの話が出てこなくなった。
r009n1|suzune|そこは濡れてない。荷物も下ろせよ。立ったままだと、あたしが板を運べない。
r009n2|yuzuha|ありがとうございます。何か、お手伝いをしたほうが。
r009n3|akari|じゃあ、これを混ぜてて。焦げるから、鍋の底まで。力はいらないよ。
r009n4|narrator|ユズハは言われたとおりに杓文字を動かした。社の地下を調べるはずが、初日は鍋の底ばかり見ていた。
r010|narrator|ユズハは器を受け取った。見返りを聞く前に、カザマルが自分の分を食べ終える。
r011|kazamaru|おかわりない？
r012|akari|ない。その子の分、見ないの。
r012n1|kazamaru|見ただけだろ。取るとは言ってねえし。
r012n2|suzune|その顔をすると、客が遠慮するんだよ。ほら、あたしの芋を半分やる。
r012n3|narrator|ユズハは自分の器を引き寄せた。返すべき借りが次々に増えるので、何をどう返せばよいのか考え始めていた。
r012n4|player|無理に話さなくていいぞ。食べてから、雨を見に行こう。
@bgm|warm
r013|narrator|その夜の報告書には、『里の警戒は薄い』と書いた。粥のことも、あなたが自分だけ小さな器を使っていたことも、書かなかった。
r014|yuzuha|……もう少し、調べてから。
r014n1|narrator|報告の欄には、社へ入った時刻と、床板を外した場所も書いた。地下への入口は見つからない。隠している様子もない。
r014n2|yuzuha|床を直していただけ。……それは、そのまま書いていいはず。
r014n3|narrator|筆を置くと、昼に配った団子の代金を精算欄へ書き足した。四人が好きな味を調べ、命じられたとおり買った。九尾へ報告する仕事は、それで終わった。
r014n4|narrator|その下に、余計なことを書きかけた。自分だけ小さな椀で粥を食べていた少年のこと。消して、代わりに自分の財布を開く。次も買うには、少し足りなかった。
r015|narrator|次に里へ行く日は晴れていた。ユズハは形の崩れた団子を包み、門の外で渡す練習を一度した。仕事の挨拶なら、練習など要らなかったのに。
`),
  ep('yuzuha-03', 'yuzuha', '第三話　出さなかった報告', '風越国・風越地方・城の客間／現在', 'bamboo', ['yuzuha', 'e030'], `
@bgm|mystery
r001|narrator|出発の朝、客間に九尾の影が現れた。一族の長である九尾が、城から術で送ってきた分身だという。その手には、古い報告書があった。
r002|e030|社の下で、何も見つからなかった。毎月そう書いていたね。
r003|yuzuha|ええ。
r004|e030|嘘だろう。
r004n1|e030|何もないという報告なら、あれほど詳しく毎月書く必要はない。何も起きていないと、思わせたかったんだろう。
r004n2|yuzuha|読まれていたのですね。では、なぜ確かめに来なかったのですか。
r004n3|e030|お前が隠す物の正体を、先に知りたかった。力ずくで掘り返して、壊してしまうのも困るからね。
r004n4|narrator|ユズハは袖の中で指を握った。里へ兵が来なかった理由を、今まで自分の報告だけだと思おうとしていた。
r005|narrator|ユズハは答えなかった。実際には一度だけ、あなたが眠る社の床から、赤い光が漏れた夜があった。
r006|e030|あの子の妖力に気づいていた。だから、何もないと書き続けた。
r007|yuzuha|正体までは、わかりませんでした。
r008|e030|なら、なぜ隠した。
r009|yuzuha|見つかったと書けば、里へ兵が来ます。それだけは、わかっていました。
r009n1|e030|お前一人で、止めていたつもりかい。私が待つのをやめていたら、どうした。
r009n2|yuzuha|……わかりません。だから、来ないように嘘を重ねました。皆さんに話すより、そのほうが楽だったんです。
r009n3|e030|では、隠していた自分も責められるね。
r009n4|yuzuha|はい。あの人たちが助かったら、私のしたことも消えると、どこかで思っていました。
@bgm|warm
r010|narrator|長は報告書を閉じた。責める声ではなく、珍しいものを見るように尋ねる。
r011|e030|見つけたものを差し出せば、お前は一族に戻れた。望んでいたはずだ。
r012|yuzuha|戻れと言われなくても帰れる家が、もうあったので。
r012n1|narrator|九尾はユズハの荷を見た。昔持たせた箱に、里で縫い直した布が掛かっている。新しい結び紐だけが、昨日のものだった。
r012n2|yuzuha|書き直してしまおうかと、考えました。帰り道でなくしても、誰にもわからないと。
r012n3|yuzuha|でも、忘れてもらうために戻るのではありません。何を持っていったか、見てもらいます。
r012n4|narrator|九尾は箱へ手を伸ばさなかった。報告書を一枚取り出し、残っていた控えも、上へ重ねた。
r013|e030|……あの子が魔王でなくても、同じことをしたか。
r014|yuzuha|そのときは、魔王ではありませんでした。
r014n1|e030|……そうだったね。王だと知る前に、命令に背いた。そのことだけは、書類ではわからなかった。
r014n2|yuzuha|書きませんでしたから。自分でも、どう書けばいいかわからなかったんです。
r014n3|narrator|ユズハは控えを受け取り、箱へ納めた。九尾が何かを言いかけても、今度は先に身を固くしなかった。
r014n4|e030|行きなさい。残った命令は、私のほうで終わらせる。お前の報告も、もう求めない。
r015|narrator|長は道をあけた。ユズハは一礼し、自分で詰めた荷物を持って門を出た。
`),
  ep('yuzuha-04', 'yuzuha', '第四話　許す前に、聞きたい', '風越国・若竹地方・帰り道の宿', 'bamboo', ['yuzuha', 'akari'], `
@bgm|sad
r501|narrator|帰りの宿で、ユズハはあなたに箱を渡した。報告書も命令書も、全部入っているという。カザマルとスズネは眠り、アカリだけが部屋の隅で起きていた。
r502|yuzuha|読んでください。私が、里で何をしていたか。
r503|narrator|箱は思ったより軽かった。その中に、里へ出入りする時刻、見張りの癖、誰と親しいかが、何年ぶんも詰まっていた。
r504|player|よく、見ていたな。
r505|yuzuha|そういう仕事でした。
r506|narrator|言ったあと、ユズハは唇を噛んだ。あなたは頁をめくった。次には、アカリの家の戸が、いつ開いているかまで書いてあった。
r507|player|これも仕事か。
r508|yuzuha|……はい。
r509|narrator|雨の日に泊めてもらった、とある。その日の夕方、あなたもアカリの家にいた。五人で濡れた服を干し、窓の紐を取り合った記憶があった。
r510|player|あの日も、夜にこれを書いていたのか。
r511|yuzuha|皆さんが、寝てから。
r512|narrator|アカリが小さく息を吸った。ユズハはそちらを向けなかった。あなたは紙を折りかけ、元へ戻した。破っても何も変わらない。
r513|yuzuha|里へは、戻りません。許していただけないことは、わかっています。
r514|player|誰が出ていけと言った。
r515|yuzuha|ですが、これだけのことをして、皆さんとまた同じ部屋にいるのは。
r516|player|いるのが辛いのか。お前も。
r517|narrator|ユズハは答えなかった。アカリが灯りの芯を直した。顔を上げないまま、それでも二人の間が暗くならないようにした。
r518|yuzuha|……はい。顔を見て、話すのが怖いです。どこかへ行けば、謝る言葉だけ残せますから。
r519|player|俺は、手紙に腹を立てるのも嫌だ。言うなら、お前に言いたい。
r520|narrator|最後の命令書を開くと、『社の住人を里から連れ出し、地下を調べよ』とあった。日付は、あなたが熱で寝込んだ冬だった。
r521|player|あのとき、お前が薬を取りに行った。
r522|yuzuha|町で診せると言えば、連れ出せました。皆さんも、たぶん信じたと思います。
r523|akari|信じたよ。薬を見つけてきたって、私、嬉しかったもの。
r524|narrator|ユズハの肩が震えた。アカリはその先を言わなかった。あなたは命令書の下に重ねてあった、その月の報告の控えを開いた。
r525|player|『まだ運べる状態にない』。俺、すぐ治ったぞ。
r526|yuzuha|治ったあとも、そう書きました。……薬だけ持って、里へ戻ったんです。
r527|player|なぜ。
r528|narrator|ユズハは口を開いた。疑われるから。時機が悪かったから。言い慣れた説明が浮かんだことが、その顔に出た。
r529|yuzuha|帰ったら、お粥を嫌がるだろうと思って。……あなた、苦い薬のあとも、甘い物が欲しいと言うから。途中で飴を買いました。
r530|narrator|覚えていた。熱のせいで味がわからないと言うと、ユズハが少しだけ悲しそうにした。寝込んだ間に聞いた中で、一番普通の話だった。
r531|player|あれは、九尾の金で買ったのか。
r532|yuzuha|いいえ。あれは……。
r533|narrator|最後まで言えなくなったユズハへ、あなたは布を差し出した。受け取らないので箱の上へ置いた。顔を拭くまで、命令書は読まなかった。
r534|player|腹は立つ。助けてもらったことを思い出しても、まだ立つ。今すぐ、何でもない顔はできん。
r535|yuzuha|はい。
r536|player|もう一つだけ聞く。今も続けている仕事はあるか。
r537|yuzuha|ありません。これで全部です。
r538|akari|明日、カザマルとスズネにも言ってね。私、代わりには話せないから。
r539|narrator|ユズハは頷いた。箱はあなたの膝に残った。返すのも、しまうのも早すぎる気がして、しばらく三人とも動かなかった。
@bgm|warm
r540|player|明日の道は、右でよかったか。昼、地図を見ていただろう。
r541|yuzuha|……はい。左は、雨で崩れています。
r542|narrator|あなたは地図を床へ広げた。ユズハは箱と地図を見比べたあと、少しだけ近くへ座った。触れそうだった肩は、まだ離れたままだった。
r543|akari|ここ、暗いね。
r544|narrator|アカリが灯りを寄せた。道を指すユズハの指から、影がなくなった。三人は少しずつ言葉を足し、明日どこで休むかを決めた。
`),
  ep('yuzuha-05', 'yuzuha', '第五話　隣の席を選ぶ', '幽京国・王城／本編終章のあと', 'capital', ['yuzuha', 'e030'], `
@bgm|warm
r001|narrator|即位を終えた王城で、九尾の影がユズハを訪ねた。昔の報告書ではなく、一族の印がついた新しい扇を持っていた。
r002|e030|一族に、お前の席を用意した。今度は、こちらから頼む。
r003|yuzuha|ありがとうございます。でも、お受けできません。
r003n1|e030|今度は見張りの役ではない。会議の席だ。お前の言葉で、一族の決め事へ口を出せる。
r003n2|yuzuha|以前なら、すぐに受けたと思います。呼ばれるのを、ずっと待っていましたから。
r003n3|e030|その席を、ようやく空けてきたのだけれどね。
r003n4|narrator|ユズハは扇の印を見た。欲しかった物が目の前にあっても、手が勝手に伸びることはなかった。
r004|e030|四天王の席のほうが上か。
r005|yuzuha|あの人の隣に、私の椅子があるんです。座りやすいように、友達が直してくれました。
r005n1|e030|椅子なら、こちらも直させる。尾が窮屈なら、初めから形を変えればよい。
r005n2|yuzuha|カザマルの羽が当たるから、と始めたのに、いつの間にか私の尾の話になっていました。私も、そこに座ることになっていたんです。
r005n3|narrator|九尾は扇を閉じた。ユズハの言葉を、皮肉だと受け取ることもできたが、言い返さずに続きを待った。
r005n4|yuzuha|……あの椅子を、空けたくありません。
r006|narrator|長は扇を開き、笑った。ユズハが子供の頃には、褒められたのかどうかわからなかった笑い方だった。
r007|e030|そうか。なら、一族が困ったときは、王に取り次いでおくれ。
r008|yuzuha|内容を聞いてから、私が判断します。
r008n1|e030|同じ狐の頼みでもかい。
r008n2|yuzuha|同じ狐でも。泣きつかれたら困りますけど、たぶん、断ります。
r008n3|e030|では、断られることもあるわけだ。少し、頼みにくくなったね。
r008n4|yuzuha|少しだけなら、困っていただいてもよいかと。私もずいぶん、困らされましたから。
r009|e030|……本当に、いい主を選んだね。
@scene|capital|王城・四天王の控えの間|yuzuha,akari
@bgm|daily
r010|narrator|長が帰ると、控えの間には五人分の茶があった。アカリが空いた席を叩く。あなたは隣で、古い報告書の箱を持っていた。
r011|player|もう持っていてもいいだろう。
r012|yuzuha|まだ、預かっていてください。私が持つと、読み返しているうちに都合のよい言い訳をつけそうです。
r013|player|わかった。
r014|akari|じゃあ、こっちはユズハが持ってて。新しい帳面、余ったから。
r014n1|narrator|角に狐の刺繍があった。余った物ではなかった。ユズハはそこを親指で擦り、礼を言う前に頁を開いた。
r014n2|yuzuha|何を書いても、いいんですか。
r014n3|player|お前の帳面だろう。……俺の寝言は、できれば書かないでくれ。
r014n4|narrator|ユズハは日付を記し、少し考えた。最初の行は、なかなか埋まらなかった。アカリは覗きたそうにしながら、茶を飲んで待った。
r015|narrator|『今日は、嬉しいことがあった』。書いた途端、ユズハは自分の字を見て笑ってしまった。下手な報告より、よほど恥ずかしかった。
r016|player|どうした。変なことでも書いたか。
r017|yuzuha|読ませません。……そのうち、私が忘れたら、読ませてあげます。
r018|narrator|あなたが覗き込み、ほかの三人も寄ってきた。ユズハは帳面を胸へ抱えた。隠しているのに、今度は誰も傷つかない。そのことが、また可笑しかった。
`),
];
export const STORY_BY_ID: Record<string, StoryEpisode> = Object.assign(Object.create(null), Object.fromEntries(STORY_EPISODES.map(s => [s.id, s])));
export function storyLine(storyId: string, lineId: string) { return STORY_BY_ID[storyId]?.lines.find(l => l.id === lineId); }
